<template>
  <div class="bdx-report">
    <div class="bdx-report__backdrop">
      <div v-if="loadError" class="bdx-load-error">
        <span>{{ loadError }}</span>
        <button
          type="button"
          class="bdx-load-error__dismiss"
          aria-label="Dismiss error"
          @click="dismissLoadError"
        >&times;</button>
      </div>

      <!-- Top bar: logo, BDX/Claim status toggles, KPI tiles, flag description -->
      <div class="bdx-topbar">
        <div class="bdx-topbar__brand">
          <div class="bdx-header__pill">
            <span class="bdx-header__pill-text">Validation</span>
          </div>
        </div>

        <div class="bdx-toggle-group">
          <div class="bdx-toggle-group__label-row">
            <span class="bdx-toggle-group__label">BDX ({{ bdxStatusLabel }})</span>
            <span
              v-if="'active' !== bdxStatus"
              class="bdx-filter-clear-icon"
              role="button"
              title="Clear filter"
              @click="clearBdxStatusFilter"
            >
              <b-icon icon="eraser" font-scale="1"></b-icon>
            </span>
          </div>
          <div class="bdx-tristate" :class="bdxStatusTristateClass" @click="advanceBdxStatus">
            <div class="bdx-tristate__knob"></div>
          </div>
        </div>

        <div class="bdx-toggle-group">
          <div class="bdx-toggle-group__label-row">
            <span class="bdx-toggle-group__label">{{ claimStatusLabel }}</span>
            <span
              v-if="'all' !== claimStatus"
              class="bdx-filter-clear-icon"
              role="button"
              title="Clear filter"
              @click="clearClaimStatusFilter"
            >
              <b-icon icon="eraser" font-scale="1"></b-icon>
            </span>
          </div>
          <div class="bdx-tristate" :class="claimStatusTristateClass" @click="advanceClaimStatusToggle">
            <div class="bdx-tristate__knob"></div>
          </div>
        </div>

        <div class="bdx-toggle-group">
          <div class="bdx-toggle-group__label-row">
            <span class="bdx-toggle-group__label">{{ severityLabel }}</span>
            <span
              v-if="filters.severity.length"
              class="bdx-filter-clear-icon"
              role="button"
              title="Clear filter"
              @click="clearSeverityFilter"
            >
              <b-icon icon="eraser" font-scale="1"></b-icon>
            </span>
          </div>
          <div class="bdx-tristate" :class="severityTristateClass" @click="advanceSeverityToggle">
            <div class="bdx-tristate__knob"></div>
          </div>
        </div>

        <div class="bdx-kpi-row">
          <div v-for="kpi in kpiTiles" :key="kpi.key" class="bdx-kpi">
            <div class="bdx-kpi__label">{{ kpi.label }}</div>
            <div class="bdx-kpi__value">{{ kpi.value.toLocaleString() }}</div>
          </div>
        </div>

        <div class="bdx-flag-description">
          <p class="bdx-flag-description__text">
            <span class="bdx-flag-description__body">{{ flagDescriptionText }}</span>
          </p>
        </div>

        <button type="button" class="bdx-clear-filters-icon" title="Clear Filters" @click="clearFilters">
          <img :src="clearFiltersIcon" alt="Clear Filters" />
        </button>

        <!-- Latest UMRs for the current filters (see fetchUmrs in setup()) -
             most commonly just the selected Report Period, per GET /umrs'
             own docstring. -->
        <b-dropdown
          variant="link"
          no-caret
          class="bdx-notifications-dropdown"
          toggle-class="bdx-notifications-toggle"
          menu-class="bdx-checklist-menu bdx-notifications-menu"
          right
        >
          <template #button-content>
            <span class="bdx-notifications-icon" title="Notifications">
              <img :src="notificationsIcon" alt="Notifications" />
            </span>
          </template>
          <b-dropdown-form @click.stop @submit.prevent>
            <div class="bdx-checklist-search bdx-checklist-search--icon-only">
              <span
                v-if="hasUmrColumnFilters"
                class="bdx-filter-clear-icon"
                role="button"
                title="Clear all column filters"
                @click="clearUmrColumnFilters"
              >
                <b-icon icon="eraser" font-scale="0.9"></b-icon>
              </span>
              <span class="bdx-download-icon" role="button" title="Export data" @click="downloadUmrsExcel">
                <b-icon icon="download" font-scale="0.9"></b-icon>
              </span>
            </div>
            <div v-if="loadingUmrs" class="bdx-checklist-empty">Loading…</div>
            <template v-else>
              <table v-if="umrsList.length" class="bdx-notifications-table">
                <thead>
                  <tr>
                    <th v-for="col in umrColumns" :key="col.key">
                      <div class="bdx-th-inner">
                        <span class="bdx-th-label">{{ col.label }}</span>
                        <span class="bdx-th-actions">
                          <span
                            class="bdx-th-search-toggle"
                            role="button"
                            tabindex="0"
                            @click.stop="umrColumnSearchMenuRef.toggle(col.key, $event)"
                            @keydown.enter.stop="umrColumnSearchMenuRef.toggle(col.key, $event)"
                          >
                            <b-icon v-if="!umrColumnSearch[col.key]" icon="search" font-scale="0.8"></b-icon>
                            <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                          </span>
                          <span class="bdx-th-sort-icon" role="button" @click.stop="toggleUmrColumnSort(col.key)">
                            <span
                              class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                              :class="{ 'bdx-th-sort-icon__caret--active': umrSortBy === col.key && !umrSortDesc }"
                            ></span>
                            <span
                              class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                              :class="{ 'bdx-th-sort-icon__caret--active': umrSortBy === col.key && umrSortDesc }"
                            ></span>
                          </span>
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!displayedUmrsList.length">
                    <td :colspan="umrColumns.length">No rows match your search.</td>
                  </tr>
                  <tr
                    v-for="umr in displayedUmrsList"
                    :key="umr.limeiq_key + '|' + umr.report_period"
                    class="bdx-notifications-row"
                    :class="{ 'bdx-notifications-row--selected': isUmrRowSelected(umr) }"
                    @click="selectUmrRow(umr)"
                  >
                    <td>{{ umr.limeiq_key }}</td>
                    <td>{{ umr.report_period ? formatReportPeriodLabel(umr.report_period) : '—' }}</td>
                    <td>{{ umr.critical_count.toLocaleString() }}</td>
                    <td>{{ umr.non_critical_count.toLocaleString() }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="bdx-checklist-empty">No UMRs found</div>
            </template>
          </b-dropdown-form>
        </b-dropdown>
        <rec-column-search-menu
          ref="umrColumnSearchMenuRef"
          :column-search="umrColumnSearch"
          allow-blank
          @search="setUmrColumnSearch"
        />
      </div>

      <!-- Body: left sidebar / center tables / right flag panels -->
      <div class="bdx-body">
        <aside class="bdx-sidebar">
          <div class="bdx-sidebar__section bdx-sidebar__section--periods">
            <div class="bdx-sidebar__label">
              <span>Report Period</span>
              <span
                v-if="selectedPeriods.length"
                class="bdx-filter-clear-icon"
                role="button"
                title="Clear filter"
                @click="selectedPeriods = []"
              >
                <b-icon icon="eraser" font-scale="1"></b-icon>
              </span>
            </div>
            <div class="bdx-period-tree">
              <label class="bdx-check">
                <input type="checkbox" :checked="allPeriodsSelected" @change="toggleAllPeriods" />
                Select all
              </label>
              <div v-for="group in periodTree" :key="group.year" class="bdx-period-year">
                <label class="bdx-check bdx-check--year">
                  <b-icon
                    :icon="isYearExpanded(group.year) ? 'chevron-down' : 'chevron-right'"
                    font-scale="0.8"
                    class="bdx-check__chevron"
                    role="button"
                    @click.prevent="toggleYearExpanded(group.year)"
                  ></b-icon>
                  <input
                    type="checkbox"
                    :checked="isYearSelected(group)"
                    :indeterminate.prop="isYearPartiallySelected(group)"
                    @change="toggleYear(group)"
                  />
                  {{ group.year }}
                </label>
                <template v-if="isYearExpanded(group.year)">
                  <label v-for="month in group.months" :key="month.value" class="bdx-check bdx-check--indent">
                    <input type="checkbox" v-model="selectedPeriods" :value="month.value" />
                    {{ month.label }}
                  </label>
                </template>
              </div>
            </div>
          </div>
        </aside>

        <div class="bdx-center">
          <div class="bdx-filter-row">
            <div class="bdx-filter bdx-filter--grow bdx-filter--grow-sm">
              <div class="bdx-filter__label">
                <span>Party</span>
                <span
                  v-if="filters.party.length"
                  class="bdx-filter-clear-icon"
                  role="button"
                  title="Clear filter"
                  @click="clearPartyFilter"
                >
                  <b-icon icon="eraser" font-scale="1"></b-icon>
                </span>
              </div>
              <b-dropdown
                variant="link"
                no-caret
                toggle-class="bdx-checklist-toggle"
                menu-class="bdx-checklist-menu bdx-checklist-menu--wide"
                @hidden="closePartyFlyout"
              >
                <template #button-content>
                  <span class="bdx-checklist-toggle__text">{{ partySummaryText }}</span>
                  <b-icon icon="chevron-down" font-scale="0.7" class="ml-1"></b-icon>
                </template>
                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredPartyGroups">
                  <div class="bdx-checklist-search">
                    <b-icon icon="search" font-scale="0.8"></b-icon>
                    <input type="text" v-model="partySearch" placeholder="Search" class="bdx-checklist-search__input" />
                  </div>
                  <label class="bdx-check">
                    <input type="checkbox" :checked="allPartySelected" @change="toggleAllParty" />
                    Select all
                  </label>
                  <div
                    v-for="group in filteredPartyGroups"
                    :key="group.partyType"
                    class="bdx-check bdx-check--year bdx-party-row"
                    :class="{ 'bdx-party-row--active': openPartyType === group.partyType }"
                    @click="togglePartyFlyout(group.partyType, $event)"
                  >
                    <b-icon
                      :icon="openPartyType === group.partyType ? 'chevron-down' : 'chevron-right'"
                      font-scale="0.8"
                      class="bdx-check__chevron"
                    ></b-icon>
                    <input
                      type="checkbox"
                      :checked="isPartyTypeSelected(group)"
                      :indeterminate.prop="isPartyTypePartiallySelected(group)"
                      :disabled="!group.names.length"
                      @click.stop
                      @change="togglePartyType(group)"
                    />
                    {{ partyTypeLabel(group.partyType) }} ({{ group.names.length }})
                  </div>
                </b-dropdown-form>
              </b-dropdown>

              <!-- Portal target - moved to <body> on mount (see onMounted/onBeforeUnmount) so it
                   escapes the dropdown menu's own overflow/Popper clipping, same reasoning as
                   RecProductionReport.vue's own dcaFlyoutRef. v-show (not v-if) keeps this
                   element's identity stable across opens/closes, since the portal only runs once. -->
              <div
                v-show="openPartyType"
                ref="partyFlyoutRef"
                class="bdx-party-flyout"
                :style="partyFlyoutStyle"
                @click.stop
                @focusin.stop
              >
                <div class="bdx-party-flyout__header">{{ partyTypeLabel(openPartyType) }}</div>
                <div class="bdx-checklist-search">
                  <b-icon icon="search" font-scale="0.8"></b-icon>
                  <input
                    type="text"
                    v-model="partyFlyoutSearch"
                    placeholder="Search"
                    class="bdx-checklist-search__input"
                    @keydown.enter.prevent="selectAllFilteredOpenPartyTypeNames"
                  />
                </div>
                <label v-for="name in filteredOpenPartyTypeNames" :key="name" class="bdx-check">
                  <input
                    type="checkbox"
                    :checked="isPartyNameSelected(openPartyType, name)"
                    @change="togglePartyName(openPartyType, name)"
                  />
                  {{ name }}
                </label>
                <div v-if="!filteredOpenPartyTypeNames.length" class="bdx-checklist-empty">No matches</div>
              </div>
            </div>

            <div
              v-for="dd in filterDropdowns"
              :key="dd.key"
              class="bdx-filter"
              :class="{ 'bdx-filter--grow': dd.wide, 'bdx-filter--grow-lg': dd.key === 'limeIQKey' }"
            >
              <div class="bdx-filter__label">
                <span>{{ dd.label }}</span>
                <span
                  v-if="filters[dd.key].length"
                  class="bdx-filter-clear-icon"
                  role="button"
                  title="Clear filter"
                  @click="clearDropdownFilter(dd.key)"
                >
                  <b-icon icon="eraser" font-scale="1"></b-icon>
                </span>
              </div>
              <b-dropdown
                variant="link"
                no-caret
                toggle-class="bdx-checklist-toggle"
                :menu-class="['bdx-checklist-menu', { 'bdx-checklist-menu--wide': dd.wide }]"
              >
                <template #button-content>
                  <span class="bdx-checklist-toggle__text">{{ filterSummary(dd.key) }}</span>
                  <b-icon icon="chevron-down" font-scale="0.7" class="ml-1"></b-icon>
                </template>
                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredDropdownOptions(dd)">
                  <div v-if="dd.searchable" class="bdx-checklist-search">
                    <b-icon icon="search" font-scale="0.8"></b-icon>
                    <input
                      type="text"
                      v-model="dropdownSearch[dd.key]"
                      placeholder="Search"
                      class="bdx-checklist-search__input"
                    />
                  </div>
                  <label v-if="dd.options.length" class="bdx-check">
                    <input type="checkbox" :checked="isAllSelected(dd)" @change="toggleAllOptions(dd)" />
                    Select all
                  </label>
                  <label v-for="opt in filteredDropdownOptions(dd)" :key="opt" class="bdx-check">
                    <input
                      type="checkbox"
                      :checked="filters[dd.key].includes(opt)"
                      @change="toggleFilterOption(dd.key, opt)"
                    />
                    {{ opt }}
                  </label>
                  <div v-if="!filteredDropdownOptions(dd).length" class="bdx-checklist-empty">No matches</div>
                  <div v-else-if="dropdownOptionsTruncatedCount(dd)" class="bdx-checklist-empty">
                    +{{ dropdownOptionsTruncatedCount(dd).toLocaleString() }} more - type to narrow down
                  </div>
                </b-dropdown-form>
              </b-dropdown>
            </div>
          </div>

          <div class="bdx-panel bdx-panel--claim-merged">
            <div class="bdx-panel__title bdx-panel__title--with-load-more">
              <span>Applicable Validation Columns</span>
              <div v-if="reportTotalRows" class="bdx-load-more">
                <span class="bdx-load-more__count">Showing {{ mergedClaimRows.length }} of {{ reportTotalRows }} rows</span>
                <button
                  v-if="hasMoreReport"
                  type="button"
                  class="bdx-load-more__btn"
                  :disabled="loadingReportMore"
                  @click="loadMoreReport"
                >
                  {{ loadingReportMore ? 'Loading…' : `Load ${reportLoadMoreCount} more` }}
                </button>
              </div>
              <div v-else></div>
              <span class="bdx-panel-title-actions">
                <span
                  v-if="hasMergedColumnFilters"
                  class="bdx-filter-clear-icon"
                  role="button"
                  title="Clear all column filters"
                  @click="clearMergedColumnFilters"
                >
                  <b-icon icon="eraser" font-scale="1"></b-icon>
                </span>
                <span
                  class="bdx-download-icon"
                  :class="{ 'bdx-download-icon--disabled': reportExporting }"
                  role="button"
                  :title="reportExporting ? 'Exporting…' : 'Export data'"
                  @click="!reportExporting && downloadReportExcel()"
                >
                  <b-icon icon="download" font-scale="1"></b-icon>
                </span>
              </span>
            </div>
            <!-- Same treatment as RecProductionReport.vue's own Variance
                 Transaction Details table - scoped to just this table's own
                 loading state, not the whole page. -->
            <b-overlay :show="loadingReportInitial" rounded="sm" opacity="0.7" class="bdx-merged-table-overlay">
              <template #overlay>
                <div class="text-center">
                  <div class="bdx-loading-spinner" role="status" aria-label="Loading"></div>
                  <p class="mt-2 mb-0">Loading…</p>
                </div>
              </template>
              <div class="bdx-table-scroll">
                <table class="bdx-table">
                  <thead>
                    <tr>
                      <th v-for="col in visibleMergedColumns" :key="col.key">
                        <div class="bdx-th-inner" :style="mergedColumnStyle(`merged:${col.key}`)">
                          <span class="bdx-th-label">{{ col.label }}</span>
                          <!-- Claim Status's own menu - same RecInlineOptionsMenu-driven trigger
                               as the Claim Details table's identical branch, reusing the exact
                               same shared claimStatus/claimStatusMenuOptions/setClaimStatusFilter/
                               inlineOptionsMenuRef (not a separate per-table filter), per explicit
                               request. Own sort icon too (added by explicit request), same as that
                               same branch there. -->
                          <span v-if="'claim_status' === col.key" class="bdx-th-actions">
                            <span
                              class="bdx-th-search-toggle fcp-inline-options-toggle"
                              role="button"
                              tabindex="0"
                              @click.stop="
                                inlineOptionsMenuRef.toggle(
                                  'claim-status-column-filter',
                                  $event,
                                  claimStatusMenuOptions,
                                  claimStatus,
                                  setClaimStatusFilter,
                                  90
                                )
                              "
                              @keydown.enter.stop="
                                inlineOptionsMenuRef.toggle(
                                  'claim-status-column-filter',
                                  $event,
                                  claimStatusMenuOptions,
                                  claimStatus,
                                  setClaimStatusFilter,
                                  90
                                )
                              "
                            >
                              <b-icon v-if="'all' === claimStatus" icon="search" font-scale="0.8"></b-icon>
                              <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                            </span>
                            <span
                              class="bdx-th-sort-icon"
                              role="button"
                              @click.stop="toggleMergedColumnSort(col.key)"
                            >
                              <span
                                class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                                :class="{ 'bdx-th-sort-icon__caret--active': mergedSortBy === col.key && !mergedSortDesc }"
                              ></span>
                              <span
                                class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                                :class="{ 'bdx-th-sort-icon__caret--active': mergedSortBy === col.key && mergedSortDesc }"
                              ></span>
                            </span>
                          </span>
                          <!-- Claim Reference's own trigger - opens the SAME top filter row Claim
                               Reference checklist (see claimReferenceColumnMenuOpen) instead of
                               this table's own free-text popover, per explicit request. Keeps its
                               own sort icon, same reasoning as the Claim Details table's identical
                               branch. -->
                          <span v-else-if="'claim_reference' === col.key" class="bdx-th-actions">
                            <span
                              class="bdx-th-search-toggle bdx-th-claim-reference-toggle"
                              role="button"
                              tabindex="0"
                              @click.stop="toggleClaimReferenceColumnMenu($event)"
                              @keydown.enter.stop="toggleClaimReferenceColumnMenu($event)"
                            >
                              <b-icon v-if="!isMergedColumnFiltered('claim_reference')" icon="search" font-scale="0.8"></b-icon>
                              <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                            </span>
                            <span
                              class="bdx-th-sort-icon"
                              role="button"
                              @click.stop="toggleMergedColumnSort(col.key)"
                            >
                              <span
                                class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                                :class="{ 'bdx-th-sort-icon__caret--active': mergedSortBy === col.key && !mergedSortDesc }"
                              ></span>
                              <span
                                class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                                :class="{ 'bdx-th-sort-icon__caret--active': mergedSortBy === col.key && mergedSortDesc }"
                              ></span>
                            </span>
                          </span>
                          <span v-else class="bdx-th-actions">
                            <span
                              class="bdx-th-search-toggle"
                              role="button"
                              tabindex="0"
                              @click.stop="mergedColumnSearchMenuRef.toggle(col.key, $event, mergedColumnSearchType(col.key))"
                              @keydown.enter.stop="mergedColumnSearchMenuRef.toggle(col.key, $event, mergedColumnSearchType(col.key))"
                            >
                              <b-icon v-if="!isMergedColumnFiltered(col.key)" icon="search" font-scale="0.8"></b-icon>
                              <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                            </span>
                            <span
                              class="bdx-th-sort-icon"
                              role="button"
                              @click.stop="toggleMergedColumnSort(col.key)"
                            >
                              <span
                                class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                                :class="{ 'bdx-th-sort-icon__caret--active': mergedSortBy === col.key && !mergedSortDesc }"
                              ></span>
                              <span
                                class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                                :class="{ 'bdx-th-sort-icon__caret--active': mergedSortBy === col.key && mergedSortDesc }"
                              ></span>
                            </span>
                          </span>
                        </div>
                        <span
                          class="bdx-col-resize-handle"
                          @mousedown="startMergedColumnResize($event, `merged:${col.key}`)"
                        ></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!loadingReportInitial && !mergedClaimRows.length">
                      <td :colspan="visibleMergedColumns.length">No matching claims found.</td>
                    </tr>
                    <tr v-else-if="!loadingReportInitial && !displayedMergedRows.length">
                      <td :colspan="visibleMergedColumns.length">No rows match your search.</td>
                    </tr>
                    <tr
                      v-for="(row, rowIndex) in displayedMergedRows"
                      :key="`${row.claim_reference}-${rowIndex}`"
                      :class="{ 'bdx-table-row--selected': isMergedRowSelected(row) }"
                      @click="selectMergedClaim(row)"
                    >
                      <td v-for="col in visibleMergedColumns" :key="col.key">
                        <div
                          class="bdx-td-inner"
                          :style="mergedColumnStyle(`merged:${col.key}`)"
                          :title="formatCell(row[col.key], col.key)"
                        >{{ formatCell(row[col.key], col.key) }}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-overlay>
          </div>
          <rec-column-search-menu
            ref="mergedColumnSearchMenuRef"
            :column-search="mergedColumnSearch"
            allow-blank
            @search="setMergedColumnSearch"
          />
        </div>

        <!-- Spans columns 1-2 (see .bdx-panel--additional's own grid-column) -
             pulled out of .bdx-center so it can extend under .bdx-sidebar's
             own column, not just this center column's width. -->
        <div class="bdx-panel bdx-panel--additional">
          <div class="bdx-panel__title bdx-panel__title--with-load-more">
            <span>Claim Details</span>
            <div v-if="additionalTotalRows" class="bdx-load-more">
              <span class="bdx-load-more__count">
                Showing {{ additionalClaimDetailsRows.length }} of {{ additionalTotalRows }} rows
              </span>
              <button
                v-if="hasMoreAdditional"
                type="button"
                class="bdx-load-more__btn"
                :disabled="loadingAdditionalMore"
                @click="loadMoreAdditional"
              >
                {{ loadingAdditionalMore ? 'Loading…' : `Load ${additionalLoadMoreCount} more` }}
              </button>
            </div>
            <div v-else></div>
            <span class="bdx-panel-title-actions">
              <span
                v-if="hasAdditionalColumnFilters"
                class="bdx-filter-clear-icon"
                role="button"
                title="Clear all column filters"
                @click="clearAdditionalColumnFilters"
              >
                <b-icon icon="eraser" font-scale="1"></b-icon>
              </span>
              <span
                class="bdx-download-icon"
                :class="{ 'bdx-download-icon--disabled': additionalExporting }"
                role="button"
                :title="additionalExporting ? 'Exporting…' : 'Export data'"
                @click="!additionalExporting && downloadAdditionalDetailsExcel()"
              >
                <b-icon icon="download" font-scale="1"></b-icon>
              </span>
            </span>
          </div>
          <div class="bdx-table-scroll">
            <table class="bdx-table">
              <thead>
                <tr>
                  <th v-for="col in additionalClaimDetailsColumns" :key="col.key">
                    <div class="bdx-th-inner" :style="additionalColumnStyle(`additional:${col.key}`)">
                      <span class="bdx-th-label">{{ col.label }}</span>
                      <!-- Claim Status's own menu - matching RecProductionReport.vue's own
                           #head(source) column exactly (RecInlineOptionsMenu, not a <b-dropdown>),
                           per explicit request. Drives the SAME claimStatus ref the top "Claims
                           status?" toggle already does (not a separate per-column filter, and not
                           the generic client-side text search every other column gets), so
                           picking Open/Closed here narrows every table/KPI/panel via the existing
                           filter pipeline, exactly like the toggle. Now also has its own sort icon
                           (added by explicit request, after initially matching Reporting Period's
                           own no-sort treatment) - sorting alphabetically by claim status is still
                           a meaningful, independent action from which menu the column opens. -->
                      <span v-if="'claim_status' === col.key" class="bdx-th-actions">
                        <span
                          class="bdx-th-search-toggle fcp-inline-options-toggle"
                          role="button"
                          tabindex="0"
                          @click.stop="
                            inlineOptionsMenuRef.toggle(
                              'claim-status-column-filter',
                              $event,
                              claimStatusMenuOptions,
                              claimStatus,
                              setClaimStatusFilter,
                              90
                            )
                          "
                          @keydown.enter.stop="
                            inlineOptionsMenuRef.toggle(
                              'claim-status-column-filter',
                              $event,
                              claimStatusMenuOptions,
                              claimStatus,
                              setClaimStatusFilter,
                              90
                            )
                          "
                        >
                          <b-icon v-if="'all' === claimStatus" icon="search" font-scale="0.8"></b-icon>
                          <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                        </span>
                        <span
                          class="bdx-th-sort-icon"
                          role="button"
                          @click.stop="toggleAdditionalColumnSort(col.key)"
                        >
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && !additionalSortDesc }"
                          ></span>
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && additionalSortDesc }"
                          ></span>
                        </span>
                      </span>
                      <!-- Reporting Period's own trigger - no free-text/date popover: opens the
                           period-tree menu below instead (see toggleReportPeriodColumnMenu). Now
                           has its own sort icon too (added by explicit request, reversing the
                           earlier "this column has none" decision) - sort_by already accepted
                           "reporting_period" server-side (see _ADDITIONAL_CLAIM_DETAILS_SORT_
                           COLUMNS), this just exposes it in the UI, same as Claim Reference/TPA/
                           UMR above. -->
                      <span v-else-if="'reporting_period' === col.key" class="bdx-th-actions">
                        <span
                          class="bdx-th-search-toggle bdx-th-report-period-toggle"
                          role="button"
                          tabindex="0"
                          @click.stop="toggleReportPeriodColumnMenu($event)"
                          @keydown.enter.stop="toggleReportPeriodColumnMenu($event)"
                        >
                          <b-icon v-if="!isAdditionalColumnFiltered('reporting_period')" icon="search" font-scale="0.8"></b-icon>
                          <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                        </span>
                        <span
                          class="bdx-th-sort-icon"
                          role="button"
                          @click.stop="toggleAdditionalColumnSort(col.key)"
                        >
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && !additionalSortDesc }"
                          ></span>
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && additionalSortDesc }"
                          ></span>
                        </span>
                      </span>
                      <!-- Claim Reference's own trigger - opens the SAME top filter row Claim
                           Reference checklist (see claimReferenceColumnMenuOpen) instead of this
                           column's own free-text popover, per explicit request. Keeps its own sort
                           icon (unlike Claim Status/Reporting Period above) - sorting alphabetically
                           by claim reference is still a meaningful, independent action from which
                           search mechanism the column uses. -->
                      <span v-else-if="'claim_reference' === col.key" class="bdx-th-actions">
                        <span
                          class="bdx-th-search-toggle bdx-th-claim-reference-toggle"
                          role="button"
                          tabindex="0"
                          @click.stop="toggleClaimReferenceColumnMenu($event)"
                          @keydown.enter.stop="toggleClaimReferenceColumnMenu($event)"
                        >
                          <b-icon v-if="!isAdditionalColumnFiltered('claim_reference')" icon="search" font-scale="0.8"></b-icon>
                          <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                        </span>
                        <span
                          class="bdx-th-sort-icon"
                          role="button"
                          @click.stop="toggleAdditionalColumnSort(col.key)"
                        >
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && !additionalSortDesc }"
                          ></span>
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && additionalSortDesc }"
                          ></span>
                        </span>
                      </span>
                      <!-- TPA's own trigger - opens the Party dropdown's own TPA section (see
                           tpaColumnMenuOpen) instead of this column's free-text popover, per
                           explicit request ("the party filter already gives us the list of TPAs").
                           Keeps its own sort icon, same reasoning as Claim Reference above. -->
                      <span v-else-if="'tpa' === col.key" class="bdx-th-actions">
                        <span
                          class="bdx-th-search-toggle bdx-th-tpa-toggle"
                          role="button"
                          tabindex="0"
                          @click.stop="toggleTpaColumnMenu($event)"
                          @keydown.enter.stop="toggleTpaColumnMenu($event)"
                        >
                          <b-icon v-if="!isAdditionalColumnFiltered('tpa')" icon="search" font-scale="0.8"></b-icon>
                          <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                        </span>
                        <span
                          class="bdx-th-sort-icon"
                          role="button"
                          @click.stop="toggleAdditionalColumnSort(col.key)"
                        >
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && !additionalSortDesc }"
                          ></span>
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && additionalSortDesc }"
                          ></span>
                        </span>
                      </span>
                      <!-- UMR's own trigger - opens the dedicated umrColumnMenuOpen checklist (see
                           its own comment) instead of this column's free-text popover, per explicit
                           request ("I want actual UMRs as a dropdown from UMR column in claim
                           details table"). Keeps its own sort icon, same reasoning as Claim
                           Reference/TPA above. -->
                      <span v-else-if="'umr' === col.key" class="bdx-th-actions">
                        <span
                          class="bdx-th-search-toggle bdx-th-umr-toggle"
                          role="button"
                          tabindex="0"
                          @click.stop="toggleUmrColumnMenu($event)"
                          @keydown.enter.stop="toggleUmrColumnMenu($event)"
                        >
                          <b-icon v-if="!isAdditionalColumnFiltered('umr')" icon="search" font-scale="0.8"></b-icon>
                          <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                        </span>
                        <span
                          class="bdx-th-sort-icon"
                          role="button"
                          @click.stop="toggleAdditionalColumnSort(col.key)"
                        >
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && !additionalSortDesc }"
                          ></span>
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && additionalSortDesc }"
                          ></span>
                        </span>
                      </span>
                      <!-- LimeIQ Key's own trigger - opens the SAME top filter row LimeIQ Key
                           checklist (see limeIQKeyColumnMenuOpen) instead of this column's own
                           free-text popover, per explicit request. Keeps its own sort icon, same
                           reasoning as Claim Reference/TPA/UMR above. -->
                      <span v-else-if="'limeiq_key' === col.key" class="bdx-th-actions">
                        <span
                          class="bdx-th-search-toggle bdx-th-limeiq-key-toggle"
                          role="button"
                          tabindex="0"
                          @click.stop="toggleLimeIQKeyColumnMenu($event)"
                          @keydown.enter.stop="toggleLimeIQKeyColumnMenu($event)"
                        >
                          <b-icon v-if="!isAdditionalColumnFiltered('limeiq_key')" icon="search" font-scale="0.8"></b-icon>
                          <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                        </span>
                        <span
                          class="bdx-th-sort-icon"
                          role="button"
                          @click.stop="toggleAdditionalColumnSort(col.key)"
                        >
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && !additionalSortDesc }"
                          ></span>
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && additionalSortDesc }"
                          ></span>
                        </span>
                      </span>
                      <span v-else class="bdx-th-actions">
                        <span
                          class="bdx-th-search-toggle"
                          role="button"
                          tabindex="0"
                          @click.stop="additionalColumnSearchMenuRef.toggle(col.key, $event, additionalColumnSearchType(col.key))"
                          @keydown.enter.stop="additionalColumnSearchMenuRef.toggle(col.key, $event, additionalColumnSearchType(col.key))"
                        >
                          <b-icon v-if="!isAdditionalColumnFiltered(col.key)" icon="search" font-scale="0.8"></b-icon>
                          <img v-else :src="filterActiveIcon" alt="Filtered" class="bdx-th-filter-icon" />
                        </span>
                        <span
                          class="bdx-th-sort-icon"
                          role="button"
                          @click.stop="toggleAdditionalColumnSort(col.key)"
                        >
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--up"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && !additionalSortDesc }"
                          ></span>
                          <span
                            class="bdx-th-sort-icon__caret bdx-th-sort-icon__caret--down"
                            :class="{ 'bdx-th-sort-icon__caret--active': additionalSortBy === col.key && additionalSortDesc }"
                          ></span>
                        </span>
                      </span>
                    </div>
                    <span
                      class="bdx-col-resize-handle"
                      @mousedown="startAdditionalColumnResize($event, `additional:${col.key}`)"
                    ></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingAdditionalInitial && !additionalClaimDetailsRows.length">
                  <td :colspan="additionalClaimDetailsColumns.length">Loading…</td>
                </tr>
                <tr v-else-if="!additionalClaimDetailsRows.length">
                  <td :colspan="additionalClaimDetailsColumns.length">No matching claims found.</td>
                </tr>
                <tr v-else-if="!displayedAdditionalClaimDetailsRows.length">
                  <td :colspan="additionalClaimDetailsColumns.length">No rows match your search.</td>
                </tr>
                <tr
                  v-for="(row, rowIndex) in displayedAdditionalClaimDetailsRows"
                  :key="`${row.claim_reference}-${rowIndex}`"
                >
                  <td v-for="col in additionalClaimDetailsColumns" :key="col.key">
                    <!-- Every column is now width-constrained/ellipsized (see additionalColumnStyle
                         above), not just Loss Description - the title tooltip generalizes the same
                         way, so hovering any truncated cell still shows its full value. -->
                    <div
                      class="bdx-td-inner"
                      :style="additionalColumnStyle(`additional:${col.key}`)"
                      :title="formatCell(row[col.key], col.key)"
                    >{{ formatCell(row[col.key], col.key) }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <rec-column-search-menu
          ref="additionalColumnSearchMenuRef"
          :column-search="additionalColumnSearch"
          allow-blank
          @search="setAdditionalColumnSearch"
        />
        <rec-inline-options-menu ref="inlineOptionsMenuRef" />

        <!-- Reporting Period's own column menu - portaled to <body> on mount (see
             onMounted/onBeforeUnmount), same reasoning as bdx-party-flyout above. Reuses the
             sidebar's own period-tree markup/state verbatim (periodTree/isYearExpanded/
             toggleYearExpanded/isYearSelected/isYearPartiallySelected/toggleYear/
             allPeriodsSelected/toggleAllPeriods/selectedPeriods) so both stay in sync for free. -->
        <div
          v-show="reportPeriodColumnMenuOpen"
          ref="reportPeriodColumnMenuRef"
          class="bdx-report-period-column-menu"
          :style="reportPeriodColumnMenuStyle"
          @click.stop
          @focusin.stop
        >
          <div class="bdx-period-tree">
            <label class="bdx-check">
              <input type="checkbox" :checked="allPeriodsSelected" @change="toggleAllPeriods" />
              Select all
            </label>
            <div v-for="group in periodTree" :key="group.year" class="bdx-period-year">
              <label class="bdx-check bdx-check--year">
                <b-icon
                  :icon="isYearExpanded(group.year) ? 'chevron-down' : 'chevron-right'"
                  font-scale="0.8"
                  class="bdx-check__chevron"
                  role="button"
                  @click.prevent="toggleYearExpanded(group.year)"
                ></b-icon>
                <input
                  type="checkbox"
                  :checked="isYearSelected(group)"
                  :indeterminate.prop="isYearPartiallySelected(group)"
                  @change="toggleYear(group)"
                />
                {{ group.year }}
              </label>
              <template v-if="isYearExpanded(group.year)">
                <label v-for="month in group.months" :key="month.value" class="bdx-check bdx-check--indent">
                  <input type="checkbox" v-model="selectedPeriods" :value="month.value" />
                  {{ month.label }}
                </label>
              </template>
            </div>
          </div>
        </div>

        <!-- Claim Reference's own column menu - same portal/reasoning as the Report Period one
             above, but reused for the top filter row's own Claim Reference checklist markup
             instead (claimReferenceDropdownConfig/dropdownSearch/isAllSelected/toggleAllOptions/
             filteredDropdownOptions/toggleFilterOption/selectAllFilteredDropdownOptions/
             dropdownOptionsTruncatedCount, all reused verbatim - identical markup to the
             filterDropdowns loop's own <b-dropdown-form>, just outside a <b-dropdown> since this
             is triggered from a table column header, not the top filter row). Guarded with
             v-if="claimReferenceDropdownConfig" (unlike Report Period, which has no such guard -
             periodTree always exists): filterDropdowns is a computed array find(), so it's
             momentarily undefined before the very first render pass, and dd.searchable/dd.options
             below would throw on that undefined otherwise. -->
        <div
          v-show="claimReferenceColumnMenuOpen"
          ref="claimReferenceColumnMenuRef"
          class="bdx-claim-reference-column-menu"
          :style="claimReferenceColumnMenuStyle"
          @click.stop
          @focusin.stop
        >
          <template v-if="claimReferenceDropdownConfig">
            <div v-if="claimReferenceDropdownConfig.searchable" class="bdx-checklist-search">
              <b-icon icon="search" font-scale="0.8"></b-icon>
              <input
                type="text"
                v-model="dropdownSearch.claimReference"
                placeholder="Search"
                class="bdx-checklist-search__input"
                @keydown.enter.prevent="selectAllFilteredDropdownOptions(claimReferenceDropdownConfig)"
              />
            </div>
            <label v-if="claimReferenceDropdownConfig.options.length" class="bdx-check">
              <input
                type="checkbox"
                :checked="isAllSelected(claimReferenceDropdownConfig)"
                @change="toggleAllOptions(claimReferenceDropdownConfig)"
              />
              Select all
            </label>
            <label v-for="opt in filteredDropdownOptions(claimReferenceDropdownConfig)" :key="opt" class="bdx-check">
              <input
                type="checkbox"
                :checked="filters.claimReference.includes(opt)"
                @change="toggleFilterOption('claimReference', opt)"
              />
              {{ opt }}
            </label>
            <div v-if="!filteredDropdownOptions(claimReferenceDropdownConfig).length" class="bdx-checklist-empty">
              No matches
            </div>
            <div v-else-if="dropdownOptionsTruncatedCount(claimReferenceDropdownConfig)" class="bdx-checklist-empty">
              +{{ dropdownOptionsTruncatedCount(claimReferenceDropdownConfig).toLocaleString() }} more - type to
              narrow down
            </div>
          </template>
        </div>

        <!-- TPA column menu (Claim Details only) - same portal/reasoning as the Claim Reference
             one above, reused for the Party dropdown's own TPA section instead (tpaGroup/
             isPartyTypeSelected/togglePartyType/isPartyNameSelected/togglePartyName/
             filteredTpaColumnMenuNames/selectAllFilteredTpaColumnMenuNames - all reused/added
             alongside the Party dropdown's own existing state, not a separate filter). -->
        <div
          v-show="tpaColumnMenuOpen"
          ref="tpaColumnMenuRef"
          class="bdx-tpa-column-menu"
          :style="tpaColumnMenuStyle"
          @click.stop
          @focusin.stop
        >
          <div class="bdx-checklist-search">
            <b-icon icon="search" font-scale="0.8"></b-icon>
            <input
              type="text"
              v-model="tpaColumnMenuSearch"
              placeholder="Search"
              class="bdx-checklist-search__input"
              @keydown.enter.prevent="selectAllFilteredTpaColumnMenuNames"
            />
          </div>
          <label v-if="tpaGroup.names.length" class="bdx-check">
            <input
              type="checkbox"
              :checked="isPartyTypeSelected(tpaGroup)"
              :indeterminate.prop="isPartyTypePartiallySelected(tpaGroup)"
              @change="togglePartyType(tpaGroup)"
            />
            Select all
          </label>
          <label v-for="name in filteredTpaColumnMenuNames" :key="name" class="bdx-check">
            <input
              type="checkbox"
              :checked="isPartyNameSelected('TPA', name)"
              @change="togglePartyName('TPA', name)"
            />
            {{ name }}
          </label>
          <div v-if="!filteredTpaColumnMenuNames.length" class="bdx-checklist-empty">No matches</div>
        </div>

        <!-- UMR column menu (Claim Details only) - same portal/reasoning as the Claim Reference
             one above, backed by its own dedicated umrDropdownConfig/filters.umr cross-filter
             instead of reusing the top filter row's own state (there is no top-row UMR dropdown -
             see umrDropdownConfig's own comment for why). -->
        <div
          v-show="umrColumnMenuOpen"
          ref="umrColumnMenuRef"
          class="bdx-umr-column-menu"
          :style="umrColumnMenuStyle"
          @click.stop
          @focusin.stop
        >
          <div class="bdx-checklist-search">
            <b-icon icon="search" font-scale="0.8"></b-icon>
            <input
              type="text"
              v-model="dropdownSearch.umr"
              placeholder="Search"
              class="bdx-checklist-search__input"
              @keydown.enter.prevent="selectAllFilteredDropdownOptions(umrDropdownConfig)"
            />
          </div>
          <label v-if="umrDropdownConfig.options.length" class="bdx-check">
            <input
              type="checkbox"
              :checked="isAllSelected(umrDropdownConfig)"
              @change="toggleAllOptions(umrDropdownConfig)"
            />
            Select all
          </label>
          <label v-for="opt in filteredDropdownOptions(umrDropdownConfig)" :key="opt" class="bdx-check">
            <input
              type="checkbox"
              :checked="filters.umr.includes(opt)"
              @change="toggleFilterOption('umr', opt)"
            />
            {{ opt }}
          </label>
          <div v-if="!filteredDropdownOptions(umrDropdownConfig).length" class="bdx-checklist-empty">
            No matches
          </div>
          <div v-else-if="dropdownOptionsTruncatedCount(umrDropdownConfig)" class="bdx-checklist-empty">
            +{{ dropdownOptionsTruncatedCount(umrDropdownConfig).toLocaleString() }} more - type to narrow down
          </div>
        </div>

        <!-- LimeIQ Key column menu (Claim Details only) - same portal/reasoning as the Claim
             Reference one above, reused for the top filter row's own LimeIQ Key checklist instead
             (limeIQKeyDropdownConfig/dropdownSearch.limeIQKey/filters.limeIQKey - see
             limeIQKeyDropdownConfig's own comment). Guarded with v-if="limeIQKeyDropdownConfig"
             for the same momentarily-undefined-on-first-render reason as
             claimReferenceDropdownConfig above (both are filterDropdowns find() lookups, unlike
             umrDropdownConfig's own always-defined plain object). -->
        <div
          v-show="limeIQKeyColumnMenuOpen"
          ref="limeIQKeyColumnMenuRef"
          class="bdx-limeiq-key-column-menu"
          :style="limeIQKeyColumnMenuStyle"
          @click.stop
          @focusin.stop
        >
          <template v-if="limeIQKeyDropdownConfig">
            <div v-if="limeIQKeyDropdownConfig.searchable" class="bdx-checklist-search">
              <b-icon icon="search" font-scale="0.8"></b-icon>
              <input
                type="text"
                v-model="dropdownSearch.limeIQKey"
                placeholder="Search"
                class="bdx-checklist-search__input"
                @keydown.enter.prevent="selectAllFilteredDropdownOptions(limeIQKeyDropdownConfig)"
              />
            </div>
            <label v-if="limeIQKeyDropdownConfig.options.length" class="bdx-check">
              <input
                type="checkbox"
                :checked="isAllSelected(limeIQKeyDropdownConfig)"
                @change="toggleAllOptions(limeIQKeyDropdownConfig)"
              />
              Select all
            </label>
            <label v-for="opt in filteredDropdownOptions(limeIQKeyDropdownConfig)" :key="opt" class="bdx-check">
              <input
                type="checkbox"
                :checked="filters.limeIQKey.includes(opt)"
                @change="toggleFilterOption('limeIQKey', opt)"
              />
              {{ opt }}
            </label>
            <div v-if="!filteredDropdownOptions(limeIQKeyDropdownConfig).length" class="bdx-checklist-empty">
              No matches
            </div>
            <div v-else-if="dropdownOptionsTruncatedCount(limeIQKeyDropdownConfig)" class="bdx-checklist-empty">
              +{{ dropdownOptionsTruncatedCount(limeIQKeyDropdownConfig).toLocaleString() }} more - type to
              narrow down
            </div>
          </template>
        </div>

        <aside class="bdx-flags-panel">
          <div class="bdx-flags-tabs">
            <button
              v-for="tab in flagTabs"
              :key="tab.value"
              type="button"
              class="bdx-flags-tabs__btn"
              :class="{ 'bdx-flags-tabs__btn--active': activeFlagTab === tab.value }"
              @click="toggleFlagTab(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>

          <div
            v-if="severityToggleState !== 'noncritical'"
            class="bdx-flags-section bdx-flags-section--critical"
            :class="{ 'bdx-flags-section--solo': severityToggleState === 'critical' }"
          >
            <div class="bdx-flags-section__title bdx-flags-section__title--critical">Critical Flags</div>
            <template v-if="criticalFlags.length">
              <div class="bdx-flags-header">
                <span>Flag</span>
                <span>Critical <span class="bdx-flags-header__caret">&#9662;</span></span>
              </div>
              <div class="bdx-flags-list">
                <div
                  v-for="flag in criticalFlags"
                  :key="flag.flagColumn"
                  class="bdx-flag-row"
                  :class="{ 'bdx-flag-row--selected': isFlagSelected(flag, 'critical') }"
                  @click="selectFlag(flag, 'critical')"
                >
                  <span class="bdx-flag-row__name">{{ flag.name }}</span>
                  <span class="bdx-flag-row__barwrap">
                    <span
                      class="bdx-flag-row__bar bdx-flag-row__bar--critical"
                      :style="{ width: barWidth(flag.count, maxCriticalCount) }"
                    ></span>
                    <span
                      class="bdx-flag-row__count"
                      :class="isCountInsideBar(flag.count, maxCriticalCount) ? 'bdx-flag-row__count--inside' : 'bdx-flag-row__count--outside'"
                    >{{ flag.count.toLocaleString() }}</span>
                  </span>
                </div>
              </div>
            </template>
            <div v-else class="bdx-flags-empty">None</div>
          </div>

          <div v-if="severityToggleState !== 'critical'" class="bdx-flags-section bdx-flags-section--noncritical">
            <div class="bdx-flags-section__title bdx-flags-section__title--noncritical">Non-Critical Flags</div>
            <template v-if="nonCriticalFlags.length">
              <div class="bdx-flags-header">
                <span>Flag</span>
                <span>Non-Critical <span class="bdx-flags-header__caret">&#9662;</span></span>
              </div>
              <div class="bdx-flags-list">
                <div
                  v-for="flag in nonCriticalFlags"
                  :key="flag.flagColumn"
                  class="bdx-flag-row"
                  :class="{ 'bdx-flag-row--selected': isFlagSelected(flag, 'non-critical') }"
                  @click="selectFlag(flag, 'non-critical')"
                >
                  <span class="bdx-flag-row__name">{{ flag.name }}</span>
                  <span class="bdx-flag-row__barwrap">
                    <span
                      class="bdx-flag-row__bar bdx-flag-row__bar--noncritical"
                      :style="{ width: barWidth(flag.count, maxNonCriticalCount) }"
                    ></span>
                    <span
                      class="bdx-flag-row__count"
                      :class="isCountInsideBar(flag.count, maxNonCriticalCount) ? 'bdx-flag-row__count--inside' : 'bdx-flag-row__count--outside'"
                    >{{ flag.count.toLocaleString() }}</span>
                  </span>
                </div>
              </div>
            </template>
            <div v-else class="bdx-flags-empty">None</div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from '@vue/composition-api';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import axios from 'axios';
import { debounce } from 'lodash-es';
import * as XLSX from 'xlsx';
import BdxValidationService from '@services/bdxValidationService';
import clearFiltersIcon from '@assets/img/bdx-clear-filters-icon.png';
import notificationsIcon from '@assets/img/bdx-notifications-list-icon.png';
// Swapped in for a column's own search-toggle icon whenever that column currently has a filter
// applied (see isAdditionalColumnFilterActive/isUmrColumnFilterActive below) - pre-colored to
// $bdx-accent (PIL: sampled the original black icon's own alpha channel as a mask, filled with
// #b06a1e) rather than left black or recolored via a CSS filter, so it reads as the same "this is
// active" accent color already used everywhere else in this report (the Claim Status toggle's own
// --active class, the sort carets, etc.) without a fragile hue-rotate/invert approximation.
import filterActiveIcon from '@assets/img/bdx-filter-active-icon.png';
// Reused as-is (not re-implemented with bdx- prefixed markup like every other pattern borrowed
// from RecProductionReport.vue elsewhere in this file) - it's a small, fully generic, self-
// contained popover (own scoped CSS, portals itself to <body>) with nothing FCP-specific in it,
// so duplicating it here would just be ~150 lines of copy-pasted position/portal logic for no
// benefit. See its own file for why it's split out as its own component at all (render-cost
// isolation from the parent's reactive graph).
import RecColumnSearchMenu, { BLANK_FILTER_VALUE } from '@components/RecColumnSearchMenu';
// Same reuse-as-is reasoning as RecColumnSearchMenu above - backs Claim Status's own header menu
// (see the template), matching RecProductionReport.vue's own Source column exactly (a plain
// options list, not a checklist or free-text box).
import RecInlineOptionsMenu from '@components/RecInlineOptionsMenu';

// Column/label definitions only - static, since they mirror the API's own
// fixed schema (schemas.py's ClaimValueRow/ClaimDetailRow/
// AdditionalClaimDetailRow). Row DATA and flag/party/filter-option lists all
// come from GET calls to the BDX Validation API - see fetchFilterOptions/
// fetchFilteredData in setup().

const CLAIM_VALUES_COLUMNS = [
  { key: 'claim_reference', label: 'Claim Reference' },
  { key: 'variance_total_incurred_mismatch', label: 'Variance Total Incurred Mismatch' },
  { key: 'variance_incurred_indemnity_mismatch', label: 'Variance Incurred Indemnity Mismatch' },
  { key: 'variance_incurred_fees_mismatch', label: 'Variance Incurred Fees Mismatch' },
  { key: 'total_incurred', label: 'Total Incurred' },
  { key: 'incurred_indemnity', label: 'Incurred Indemnity' },
  { key: 'incurred_fees', label: 'Incurred Fees' },
  { key: 'total_reserve', label: 'Total Reserve' },
  { key: 'total_paid_this_month', label: 'Total Paid This Month' },
  { key: 'paid_this_month_indemnity', label: 'Paid This Month Indemnity' },
  { key: 'paid_this_month_fees', label: 'Paid This Month Fees' },
  { key: 'previously_paid_indemnity', label: 'Previously Paid Indemnity' },
  { key: 'previously_paid_fees', label: 'Previously Paid Fees' },
  { key: 'reserve_indemnity', label: 'Reserve Indemnity' },
  { key: 'reserve_fees', label: 'Reserve Fees' },
  { key: 'reserve_expenses', label: 'Reserve Expenses' },
  { key: 'reserve_tpa_fees', label: 'Reserve TPA Fees' },
  { key: 'reserve_defence_fees', label: 'Reserve Defence Fees' },
  { key: 'reserve_attorney_coverage_fees', label: 'Reserve Attorney Coverage Fees' },
  { key: 'reserve_adjuster_fees', label: 'Reserve Adjusters Fees' },
  { key: 'risk_expiry_date', label: 'Risk Expiry Date' },
  { key: 'risk_inception_date', label: 'Risk Inception Date' },
  { key: 'date_of_loss', label: 'Date of Loss' },
  { key: 'date_claim_made', label: 'Date Claim Made' },
  { key: 'date_opened', label: 'Date Opened' },
  { key: 'date_coverage_confirmed', label: 'Date Coverage Confirmed' },
  { key: 'date_claim_amount_agreed', label: 'Date Claim Amount Agreed' },
  { key: 'date_closed', label: 'Date Closed' },
  { key: 'cat_code', label: 'Cat Code' },
  { key: 'catastrophe_name', label: 'Catastrophe Name' },
  // LIQ-2307 (added 2026-09-22) - 9 columns added to support the business-provided flag-column
  // mapping's Group C. Labels match ADDITIONAL_CLAIM_DETAILS_COLUMNS' own labels for these exact
  // same fields exactly (e.g. "Binder Contract Expiry", not "...Expiry Date") - same underlying
  // mat_bdx_validation_claims column, shown in two tables, so it reads as the same field either way
  // rather than two subtly different names for the same thing.
  { key: 'policy_or_group_ref', label: 'Policy or Group Ref' },
  { key: 'loss_state', label: 'Loss State' },
  { key: 'cause_of_loss', label: 'Cause of Loss' },
  { key: 'risk_code', label: 'Risk Code' },
  { key: 'class_of_business', label: 'Class of Business' },
  { key: 'loss_description', label: 'Loss Description' },
  { key: 'currency', label: 'Currency' },
  { key: 'reason_for_denial', label: 'Reason for Denial' },
  { key: 'binder_contract_expiry', label: 'Binder Contract Expiry' },
  // LIQ-2307 (added 2026-09-23) - State_of_Filing/Certificate_Reference, newly backfilled into
  // mat_bdx_validation_claims (previously only existed on the source table).
  { key: 'state_of_filing', label: 'State of Filing' },
  { key: 'certificate_reference', label: 'Certificate Reference' },
  // Unlike the two above, this one needed no backend schema change - binder_contract_inception
  // already existed on mat_bdx_validation_claims (used by ADDITIONAL_CLAIM_DETAILS_COLUMNS), just
  // never projected onto ClaimValueRow before now.
  { key: 'binder_contract_inception', label: 'Binder Contract Inception' },
];

const CURRENCY_COLUMN_KEYS = new Set([
  'variance_total_incurred_mismatch',
  'variance_incurred_indemnity_mismatch',
  'variance_incurred_fees_mismatch',
  'total_incurred',
  'incurred_indemnity',
  'incurred_fees',
  'total_reserve',
  'total_paid_this_month',
  'paid_this_month_indemnity',
  'paid_this_month_fees',
  'previously_paid_indemnity',
  'previously_paid_fees',
  'reserve_indemnity',
  'reserve_fees',
  'reserve_expenses',
  'reserve_tpa_fees',
  'reserve_defence_fees',
  'reserve_attorney_coverage_fees',
  'reserve_adjuster_fees',
]);

const CLAIM_DETAILS_COLUMNS = [
  { key: 'claim_reference', label: 'Claim Reference' },
  { key: 'claim_status', label: 'Claim Status' },
  { key: 'denial', label: 'Denial' },
  { key: 'refer_to_uw', label: 'Refer to UW' },
];

// Claim Values & Dates and Claim Details merged into a single table (same
// claim_reference key in both) - Claim Status/Denial/Refer to UW placed right
// after Claim Reference per explicit request, then the 27 values/dates
// columns in their original order. Each column's flag-driven visibility rule
// (see visibleMergedColumns in setup()) still traces back to whichever source
// table it came from. Row DATA is no longer static here - see
// mergedClaimRows/fetchReport in setup(), sourced from GET /report.
const MERGED_COLUMNS = [
  ...CLAIM_DETAILS_COLUMNS,
  ...CLAIM_VALUES_COLUMNS.filter((c) => c.key !== 'claim_reference'),
];

// "Loss State" was listed twice in the supplied column list (once after
// Insured, once after Class of Business) - treated as a duplicate entry and
// kept only once, in the position that matches the report screenshot.
const ADDITIONAL_CLAIM_DETAILS_COLUMNS = [
  { key: 'reporting_period', label: 'Reporting Period' },
  { key: 'tpa', label: 'TPA' },
  { key: 'claim_reference', label: 'Claim Reference' },
  { key: 'claim_status', label: 'Claim Status' },
  { key: 'insured', label: 'Insured' },
  { key: 'loss_state', label: 'Loss State' },
  { key: 'umr', label: 'UMR' },
  { key: 'risk_code', label: 'Risk Code' },
  { key: 'binder_contract_inception', label: 'Binder Contract Inception' },
  { key: 'binder_contract_expiry', label: 'Binder Contract Expiry' },
  { key: 'risk_inception_date', label: 'Risk Inception Date' },
  { key: 'risk_expiry_date', label: 'Risk Expiry Date' },
  { key: 'date_of_loss', label: 'Date of Loss' },
  { key: 'date_claim_made', label: 'Date Claim Made' },
  { key: 'date_claim_opened', label: 'Date Claim Opened' },
  { key: 'date_closed', label: 'Date Closed' },
  { key: 'class_of_business', label: 'Class of Business' },
  { key: 'policy_or_group_ref', label: 'Policy or Group Ref' },
  { key: 'denial', label: 'Denial' },
  { key: 'reason_for_denial', label: 'Reason for Denial' },
  { key: 'refer_to_uw', label: 'Refer to UW' },
  { key: 'currency', label: 'Currency' },
  { key: 'cause_of_loss', label: 'Cause of Loss' },
  { key: 'loss_description', label: 'Loss Description' },
  { key: 'cat_code', label: 'Cat Code' },
  { key: 'catastrophe_name', label: 'Catastrophe Name' },
  { key: 'limeiq_key', label: 'LimeIQ Key' },
];

// Which of ADDITIONAL_CLAIM_DETAILS_COLUMNS' own search icons open a native date picker (see
// RecColumnSearchMenu's own `type` param) instead of the generic free-text box every other
// (non-date, non-reporting_period) column gets.
const ADDITIONAL_CLAIM_DETAILS_DATE_COLUMN_KEYS = new Set([
  'binder_contract_inception',
  'binder_contract_expiry',
  'risk_inception_date',
  'risk_expiry_date',
  'date_of_loss',
  'date_claim_made',
  'date_claim_opened',
  'date_closed',
]);

// Every date-typed column across both tables (Claim Values & Details' own risk/loss/claim dates
// plus ADDITIONAL_CLAIM_DETAILS_DATE_COLUMN_KEYS' 8, less the duplicates, plus date_opened/
// date_coverage_confirmed/date_claim_amount_agreed which only exist on the merged table, and
// reporting_period which only exists on Additional Claim Details) - all display as MM-DD-YYYY via
// formatCell/formatDateMDY (explicit request, 2026-09-21), in both on-screen tables and their
// Excel exports, rather than the ISO YYYY-MM-DD strings the API returns as-is.
const DATE_COLUMN_KEYS = new Set([
  ...ADDITIONAL_CLAIM_DETAILS_DATE_COLUMN_KEYS,
  'date_opened',
  'date_coverage_confirmed',
  'date_claim_amount_agreed',
  'reporting_period',
]);

// Matches app/reports/bdx_validation/schemas.py's PartyGroupOptionsResponse
// comment ("mirrors rec's own... Underwriter, Broker, TPA, Coverholder")
// exactly - the backend's dict response has no defined key order of its own
// (plain JSON object), so this is what fixes the Party dropdown's display
// order to match RecProductionReport.vue's DCA_PARTY_TYPE_ORDER.
const PARTY_TYPE_ORDER = ['Underwriter', 'Broker', 'TPA', 'Coverholder'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// GET /filter-options.report_periods is a flat list of "YYYY-MM" strings
// (no display label) - builds the {value, label} shape the Report Period
// tree already expects, e.g. "2026-07" -> "July 2026".
function formatReportPeriodLabel(yearMonth) {
  const [year, month] = yearMonth.split('-');
  const monthName = MONTH_NAMES[Number(month) - 1] || month;
  return `${monthName} ${year}`;
}

// GET /flags' FlagItem uses snake_case (flag_column, critical_type) - mapped
// to the camelCase shape the rest of this component already works with
// (flagColumn, used by selectedFlag/visibleMergedColumns/etc.), rather than
// rewriting every existing consumer to snake_case just because the wire
// format differs from how these were authored as plain static JS objects
// before the backend existed (see schemas.py's own comment acknowledging
// this exact deviation).
function mapFlagItem(item) {
  return {
    name: item.name,
    count: item.count,
    flagColumn: item.flag_column,
    group: item.group,
    description: item.description,
    criticalType: item.critical_type,
  };
}

export default defineComponent({
  name: 'BDXValidationReport',
  components: { RecColumnSearchMenu, RecInlineOptionsMenu },
  setup() {
    // Filter state <-> URL query params, same pattern as RecProductionReport.vue's own
    // initialQuery/urlQuery/router.replace (see its own comments) - reloading this page (or
    // sharing/bookmarking the URL) should come back to the same filtered view instead of resetting
    // to the defaults, since every filter here otherwise lives only in in-memory refs. Read once,
    // here, to seed each filter's initial value - not re-applied reactively on external route
    // changes (e.g. browser back/forward), which would need its own loop-prevention against the
    // write side (urlQuery/router.replace further down).
    const route = useRoute();
    const router = useRouter();
    const initialQuery = route.query;

    // Repeated-key params (?flag=A&flag=B) come back from vue-router as an array already; a single
    // occurrence (?flag=A) comes back as a bare string - this normalizes both. Every multi-select
    // filter on this report already uses [] (not null) as its own "no restriction" default, so
    // that's what an absent param falls back to as well, unlike RecProductionReport.vue's own
    // nullable variant of this same helper.
    const parseArrayQueryParam = (value) => (undefined === value ? [] : [].concat(value));

    // Tristate toggles (see .bdx-tristate in the SCSS, same pattern as
    // RecProductionReport.vue's own fcp-tristate slider): left = green/
    // active-or-open, right = red/inactive-or-closed, center = default/all.
    const bdxStatus = ref(['active', 'inactive', 'all'].includes(initialQuery.bdx_status) ? initialQuery.bdx_status : 'active');
    const claimStatus = ref(
      ['open', 'closed', 'blank', 'all'].includes(initialQuery.claim_status) ? initialQuery.claim_status : 'all'
    );
    const selectedPeriods = ref(parseArrayQueryParam(initialQuery.report_periods));
    const activeFlagTab = ref(null);
    const selectedFlag = ref(null);

    // Report Period tree state (RecProductionReport.vue's own pattern) - which
    // year groups are expanded. Populated once GET /filter-options first
    // resolves (see fetchFilterOptions) since the years/months themselves are
    // no longer a static list.
    const expandedYears = ref([]);

    // Party dropdown state. filters.party holds "PartyType::Name" compound
    // keys (not just the name) so the same company name appearing under two
    // different party types - e.g. "Marsh" is both a Broker and a Coverholder
    // in the real data - is tracked independently per type.
    const openPartyType = ref(null);
    const partyFlyoutRef = ref(null);
    const partyFlyoutStyle = reactive({ top: '0px', left: '0px' });

    // ---- Live backend data (GET /flag-column-map once, on mount only;
    // GET /party-groups + /filter-options on mount AND whenever a base
    // filter changes; GET /flags, /kpis, /report on mount AND on every
    // filter change - see fetchFlagColumnMap/fetchFilterOptions/
    // fetchFilteredData below) ----

    const loadingReference = ref(true);
    const loadingFlagsKpis = ref(true);
    const loadError = ref('');

    // LIQ-2303 - loadError previously had no way to clear once set: it's shared across all 8
    // fetch functions below and nothing ever reset it back to '', so one transient error (most
    // commonly from /umrs under rapid filter changes - see cache.py's own QueryResultCache fix,
    // same ticket) left this banner stuck for the rest of the session even after later fetches
    // succeeded and the underlying data was already showing correctly. Two-part fix: this function
    // gives the user a manual way to dismiss it (wired to the banner's new close button), and each
    // fetch function now also clears loadError.value at the START of its own attempt (before
    // awaiting), so a fresh attempt never leaves a stale error from a previous one on screen
    // regardless of whether the user notices/clicks dismiss - the banner then only ever reflects
    // the most recently INITIATED attempt's own outcome.
    function dismissLoadError() {
      loadError.value = '';
    }

    const reportPeriodOptionsList = ref([]); // [{value: 'YYYY-MM', label: 'Month YYYY'}]
    const partyGroupsList = ref([]); // [{partyType, names}]
    const claimReferenceOptionsList = ref([]);
    const limeIQKeyOptionsList = ref([]);
    const umrOptionsList = ref([]);

    const flagColumnMap = reactive({
      valueParams: {},
      detailParams: {},
      valueFailOpen: true,
      detailFailOpen: false,
    });

    const criticalFlagsList = ref([]);
    const nonCriticalFlagsList = ref([]);
    // Every flag name currently selectable in the Flag dropdown (filterDropdowns' own 'flag'
    // entry below) - narrowed by every OTHER active filter, but never by filters.flag/severity
    // themselves (self-exclusion - see fetchFlagsAndKpis' own dropdownOptionsParams comment),
    // unlike criticalFlagsList/nonCriticalFlagsList above, which DO narrow to the current
    // selection for the Critical/Non-Critical panels' own display.
    const flagDropdownOptionsList = ref([]);

    const mergedClaimRowsList = ref([]);
    const additionalClaimDetailsRowsList = ref([]);

    // Same {year, months: [{value, label}]} shape RecProductionReport.vue's
    // own report-period tree expects - built here (instead of as a computed)
    // so it's only rebuilt when the underlying option list actually changes.
    function buildPeriodTree(periodOptions) {
      const byYear = new Map();
      periodOptions.forEach((opt) => {
        const year = opt.value.split('-')[0];
        if (!byYear.has(year)) byYear.set(year, []);
        byYear.get(year).push(opt);
      });
      return [...byYear.entries()]
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([year, months]) => ({ year, months }));
    }

    const periodTree = computed(() => buildPeriodTree(reportPeriodOptionsList.value));

    // Same technique as RecProductionReport.vue: .router-view (App.vue's route
    // outlet) and its parent shell default to centering their content with a
    // content-sized height, which leaves this report unable to fill the actual
    // viewport. Tagging both for the lifetime of this component lets the global,
    // unscoped style below give .router-view a real, definite height - which is
    // what .bdx-report's own height: 100% (scoped SCSS) then fills - instead of
    // freezing a one-off pixel measurement that can't track window resizes.
    let routerView = null;
    let reportShell = null;

    onMounted(() => {
      routerView = document.querySelector('.router-view');
      if (routerView) routerView.classList.add('bdx-report-active');

      reportShell = routerView && routerView.parentElement;
      if (reportShell) reportShell.classList.add('bdx-report-shell-active');

      // Portal: same reasoning as RecProductionReport.vue's own dcaFlyoutRef -
      // needs to live at <body>, not inside the Party dropdown menu, so it can
      // escape that menu's own overflow/Popper clipping.
      if (partyFlyoutRef.value) document.body.appendChild(partyFlyoutRef.value);

      // Same portal reasoning for Reporting Period's own column menu - escapes
      // .bdx-table-scroll's own overflow: auto clipping.
      if (reportPeriodColumnMenuRef.value) document.body.appendChild(reportPeriodColumnMenuRef.value);
      document.addEventListener('mousedown', onReportPeriodColumnMenuMousedown);
      window.addEventListener('scroll', onReportPeriodColumnMenuScroll, true);
      document.addEventListener('keydown', onReportPeriodColumnMenuKeydown);

      // Same portal reasoning for Claim Reference's own column menu, shared by both tables.
      if (claimReferenceColumnMenuRef.value) document.body.appendChild(claimReferenceColumnMenuRef.value);
      document.addEventListener('mousedown', onClaimReferenceColumnMenuMousedown);
      window.addEventListener('scroll', onClaimReferenceColumnMenuScroll, true);
      document.addEventListener('keydown', onClaimReferenceColumnMenuKeydown);

      // Same portal reasoning for the TPA column menu.
      if (tpaColumnMenuRef.value) document.body.appendChild(tpaColumnMenuRef.value);
      document.addEventListener('mousedown', onTpaColumnMenuMousedown);
      window.addEventListener('scroll', onTpaColumnMenuScroll, true);
      document.addEventListener('keydown', onTpaColumnMenuKeydown);

      // Same portal reasoning for the UMR column menu.
      if (umrColumnMenuRef.value) document.body.appendChild(umrColumnMenuRef.value);
      document.addEventListener('mousedown', onUmrColumnMenuMousedown);
      window.addEventListener('scroll', onUmrColumnMenuScroll, true);
      document.addEventListener('keydown', onUmrColumnMenuKeydown);

      // Same portal reasoning for the LimeIQ Key column menu.
      if (limeIQKeyColumnMenuRef.value) document.body.appendChild(limeIQKeyColumnMenuRef.value);
      document.addEventListener('mousedown', onLimeIQKeyColumnMenuMousedown);
      window.addEventListener('scroll', onLimeIQKeyColumnMenuScroll, true);
      document.addEventListener('keydown', onLimeIQKeyColumnMenuKeydown);
    });

    onBeforeUnmount(() => {
      if (routerView) routerView.classList.remove('bdx-report-active');
      if (reportShell) reportShell.classList.remove('bdx-report-shell-active');

      if (partyFlyoutRef.value && document.body.contains(partyFlyoutRef.value)) {
        document.body.removeChild(partyFlyoutRef.value);
      }

      if (reportPeriodColumnMenuRef.value && document.body.contains(reportPeriodColumnMenuRef.value)) {
        document.body.removeChild(reportPeriodColumnMenuRef.value);
      }
      document.removeEventListener('mousedown', onReportPeriodColumnMenuMousedown);
      window.removeEventListener('scroll', onReportPeriodColumnMenuScroll, true);
      document.removeEventListener('keydown', onReportPeriodColumnMenuKeydown);

      if (claimReferenceColumnMenuRef.value && document.body.contains(claimReferenceColumnMenuRef.value)) {
        document.body.removeChild(claimReferenceColumnMenuRef.value);
      }
      document.removeEventListener('mousedown', onClaimReferenceColumnMenuMousedown);
      window.removeEventListener('scroll', onClaimReferenceColumnMenuScroll, true);
      document.removeEventListener('keydown', onClaimReferenceColumnMenuKeydown);

      if (tpaColumnMenuRef.value && document.body.contains(tpaColumnMenuRef.value)) {
        document.body.removeChild(tpaColumnMenuRef.value);
      }
      document.removeEventListener('mousedown', onTpaColumnMenuMousedown);
      window.removeEventListener('scroll', onTpaColumnMenuScroll, true);
      document.removeEventListener('keydown', onTpaColumnMenuKeydown);

      if (umrColumnMenuRef.value && document.body.contains(umrColumnMenuRef.value)) {
        document.body.removeChild(umrColumnMenuRef.value);
      }
      document.removeEventListener('mousedown', onUmrColumnMenuMousedown);
      window.removeEventListener('scroll', onUmrColumnMenuScroll, true);
      document.removeEventListener('keydown', onUmrColumnMenuKeydown);

      if (limeIQKeyColumnMenuRef.value && document.body.contains(limeIQKeyColumnMenuRef.value)) {
        document.body.removeChild(limeIQKeyColumnMenuRef.value);
      }
      document.removeEventListener('mousedown', onLimeIQKeyColumnMenuMousedown);
      window.removeEventListener('scroll', onLimeIQKeyColumnMenuScroll, true);
      document.removeEventListener('keydown', onLimeIQKeyColumnMenuKeydown);
    });

    // ---- Fetch functions (RecProductionReport.vue's own CancelToken pattern:
    // cancel whatever's still in flight before starting a new request for the
    // same data, so a fast filter change can't have an older response land
    // after - and overwrite - a newer one) ----

    // Static reference data - never depends on any filter, so fetched exactly
    // once on mount rather than on every filter change like
    // fetchFilterOptions below.
    async function fetchFlagColumnMap() {
      loadError.value = '';
      try {
        const flagColumnMapRes = await BdxValidationService.getFlagColumnMap();
        const flagColumnMapData = flagColumnMapRes.data || {};
        flagColumnMap.valueParams = flagColumnMapData.value_params || {};
        flagColumnMap.detailParams = flagColumnMapData.detail_params || {};
        flagColumnMap.valueFailOpen = flagColumnMapData.value_fail_open !== false;
        flagColumnMap.detailFailOpen = flagColumnMapData.detail_fail_open === true;
      } catch (err) {
        loadError.value =
          (err.response && err.response.data && err.response.data.detail) || 'Failed to load flag column map.';
      }
    }

    // True only for the very first call (on mount) - every later call is a
    // cross-filter refresh triggered by another filter changing, and
    // shouldn't reset which Report Period years the user has expanded/
    // collapsed in the sidebar tree.
    let isFirstFilterOptionsFetch = true;
    let filterOptionsCancelSource = null;

    // Party groups + filter-option dropdown lists (Report Period/Claim
    // Reference/LimeIQ Key) are cross-filtered by every OTHER currently-
    // applied filter (see repository.fetch_filter_options'/
    // fetch_party_group_options' own self-exclusion docstrings server-side -
    // each list is narrowed by every filter EXCEPT its own, so checking one
    // Report Period doesn't hide every other Report Period from its own
    // list). Re-run on mount AND whenever bdxStatus/claimStatus/
    // selectedPeriods/claimReference/limeIQKey/party/severity/flag change (see the watch()
    // below) - flag/severity now included too, by explicit request: Party/TPA/Claim Reference/
    // LimeIQ Key's own option lists cross-filter by whatever's currently selected in the Flag/
    // Severity dropdowns, same as every other filter already did (no circularity concern the way
    // getFlags/getKpis' OWN flag/severity narrowing has - Flag/Severity is a different field from
    // any of these four, not narrowing its own list).
    async function fetchFilterOptions() {
      loadError.value = '';
      if (filterOptionsCancelSource) filterOptionsCancelSource.cancel();
      filterOptionsCancelSource = axios.CancelToken.source();
      const cancelToken = filterOptionsCancelSource.token;
      const params = {
        ...currentFilterParams(),
        flag: filters.flag.length ? filters.flag : undefined,
        severity: filters.severity.length ? filters.severity : undefined,
      };
      const isFirstFetch = isFirstFilterOptionsFetch;
      isFirstFilterOptionsFetch = false;

      loadingReference.value = true;
      try {
        const [partyGroupsRes, filterOptionsRes] = await Promise.all([
          BdxValidationService.getPartyGroups({ ...params, cancelToken }),
          BdxValidationService.getFilterOptions({ ...params, cancelToken }),
        ]);

        const partyGroupOptions = (partyGroupsRes.data && partyGroupsRes.data.party_group_options) || {};
        // Every party type is kept even when the cross-filter narrows it to no names (shown as
        // e.g. "TPA (0)") - dropping empty sections made TPA/Broker look like they'd vanished
        // from the dropdown after selecting a Lead Underwriter (LIQ-2443).
        partyGroupsList.value = PARTY_TYPE_ORDER.map((partyType) => ({
          partyType,
          names: [...new Set(partyGroupOptions[partyType] || [])],
        }));

        const filterOptionsData = filterOptionsRes.data || {};
        reportPeriodOptionsList.value = (filterOptionsData.report_periods || [])
          .slice()
          .sort()
          .reverse()
          .map((value) => ({ value, label: formatReportPeriodLabel(value) }));
        if (isFirstFetch) expandedYears.value = periodTree.value.map((group) => group.year);
        // Deduplicated defensively - claim_reference/limeiq_key aren't
        // guaranteed distinct-per-value from this endpoint (the same claim
        // can appear once per reporting period - see fetchReport's own
        // duplicate-claim_reference comment), and a checklist dropdown
        // showing the same option twice both confuses the UI and produces
        // duplicate :key values in its own v-for.
        claimReferenceOptionsList.value = [...new Set(filterOptionsData.claim_reference || [])];
        limeIQKeyOptionsList.value = [...new Set(filterOptionsData.limeiq_key || [])];
        umrOptionsList.value = [...new Set(filterOptionsData.umr || [])];
      } catch (err) {
        if (axios.isCancel(err)) return;
        loadError.value =
          (err.response && err.response.data && err.response.data.detail) || 'Failed to load filter options.';
      } finally {
        loadingReference.value = false;
      }
    }

    // Maps this component's own filter state to the API's query params -
    // tristate toggles collapse to a single value or undefined ("all", no
    // filter sent), multi-select checklists send their array only when
    // non-empty. dca_selections is filters.party unchanged - the Party
    // dropdown's own "PartyType::Name" keys already are the composite format
    // the backend expects.
    function currentFilterParams() {
      return {
        bdx_status: bdxStatus.value === 'active' ? 'Active' : bdxStatus.value === 'inactive' ? 'Inactive' : undefined,
        claim_status:
          claimStatus.value === 'open'
            ? 'open'
            : claimStatus.value === 'closed'
            ? 'closed'
            : claimStatus.value === 'blank'
            ? 'blank'
            : undefined,
        report_periods: selectedPeriods.value.length ? selectedPeriods.value : undefined,
        claim_reference: filters.claimReference.length ? filters.claimReference : undefined,
        limeiq_key: filters.limeIQKey.length ? filters.limeIQKey : undefined,
        umr: filters.umr.length ? filters.umr : undefined,
        dca_selections: filters.party.length ? filters.party : undefined,
      };
    }

    let flagsKpisCancelSource = null;

    async function fetchFlagsAndKpis() {
      loadError.value = '';
      if (flagsKpisCancelSource) flagsKpisCancelSource.cancel();
      flagsKpisCancelSource = axios.CancelToken.source();
      const cancelToken = flagsKpisCancelSource.token;
      // KPI tiles narrow by flag/severity (by explicit request, diverging from the live Power BI
      // report's own Flag/Severity slicers, which don't actually affect these cards despite
      // looking like they should - see the backend's GET /kpis docstring). The Critical/
      // Non-Critical panels and the Flag dropdown's own option list (filterDropdowns' 'flag'
      // entry - see flagDropdownOptionsList below) deliberately do NOT: both read the flag
      // catalog itself, so narrowing either by the CURRENT flag/severity selection hid every
      // OTHER flag from view - reported live twice, once for the dropdown (checking one flag left
      // it the only pickable option) and once for the panels (clicking/checking a flag collapsed
      // the list down to just that one, with no way to see or pick a different one). Selected
      // flags stay visible in both now, just highlighted - see isFlagSelected.
      const kpiParams = {
        ...currentFilterParams(),
        flag: filters.flag.length ? filters.flag : undefined,
        severity: filters.severity.length ? filters.severity : undefined,
      };
      const flagsParams = currentFilterParams();

      loadingFlagsKpis.value = true;
      try {
        const [flagsRes, kpisRes] = await Promise.all([
          BdxValidationService.getFlags({ ...flagsParams, cancelToken }),
          BdxValidationService.getKpis({ ...kpiParams, cancelToken }),
        ]);

        const flagsData = flagsRes.data || {};
        criticalFlagsList.value = (flagsData.critical || []).map(mapFlagItem);
        nonCriticalFlagsList.value = (flagsData.non_critical || []).map(mapFlagItem);
        flagDropdownOptionsList.value = [...criticalFlagsList.value, ...nonCriticalFlagsList.value].map(
          (f) => f.name
        );

        // Reconstructs selectedFlag (description/column-visibility drill-down state - see
        // selectFlag's own comment) from filters.flag once its actual flag object becomes
        // available - needed only right after a reload/shared link restored filters.flag from the
        // URL (see initialQuery above) without the full flag object selectFlag() itself would
        // normally capture directly. Guarded to run at most once: any later real fetch already has
        // selectedFlag either set (from a manual selectFlag click) or genuinely null (filters.flag
        // also [] in that case, or holding more than one name from a dropdown multi-select, which
        // this deliberately never reconstructs a single selectedFlag from), so this never
        // overwrites a live selection. criticalFlagsList/nonCriticalFlagsList are always
        // unrestricted now, so no circularity concern reading them here either.
        if (!selectedFlag.value && 1 === filters.flag.length) {
          const criticalMatch = criticalFlagsList.value.find((f) => f.name === filters.flag[0]);
          const nonCriticalMatch = nonCriticalFlagsList.value.find((f) => f.name === filters.flag[0]);
          if (criticalMatch) selectedFlag.value = { ...criticalMatch, severity: 'critical' };
          else if (nonCriticalMatch) selectedFlag.value = { ...nonCriticalMatch, severity: 'non-critical' };
        }

        const kpisData = kpisRes.data || {};
        kpis.bdxWithFlag = kpisData.bdx_with_flag || 0;
        kpis.claimsWithFlag = kpisData.claims_with_flag || 0;
        kpis.bdxWithCriticalFlag = kpisData.bdx_with_critical_flag || 0;
        kpis.claimsWithCriticalFlags = kpisData.claims_with_critical_flags || 0;
      } catch (err) {
        if (axios.isCancel(err)) return;
        loadError.value =
          (err.response && err.response.data && err.response.data.detail) || 'Failed to load flags/KPIs.';
      } finally {
        loadingFlagsKpis.value = false;
      }
    }

    // Backs the bell icon's notification dropdown - same filter set
    // and cross-filter timing as fetchFlagsAndKpis (both share
    // currentFilterParams() and the same "base filters" watch() group below),
    // kept as its own fetch/cancel-source pair since it's conceptually
    // unrelated to the flag panels/KPI tiles it happens to be triggered
    // alongside.
    let umrsCancelSource = null;
    const umrsList = ref([]);
    const loadingUmrs = ref(false);

    async function fetchUmrs() {
      loadError.value = '';
      if (umrsCancelSource) umrsCancelSource.cancel();
      umrsCancelSource = axios.CancelToken.source();
      const cancelToken = umrsCancelSource.token;
      // limeiq_key self-excluded, but ONLY when umrRowFlagFilter is true - that ref reliably
      // tracks whether the CURRENT filters.limeIQKey selection came from clicking a row in THIS
      // same table (selectUmrRow sets both together) vs. from the LimeIQ Key checklist dropdown
      // elsewhere (toggleFilterOption/toggleAllOptions/clearDropdownFilter/clearFilters all reset
      // umrRowFlagFilter to false - see its own comment above). Unconditionally omitting
      // limeiq_key here (an earlier version of this fix) stopped the self-select-hides-every-
      // other-row loop, but also broke the LimeIQ Key dropdown's own, legitimate narrowing of
      // this table - reported live. Only the row-click case needs excluding; the dropdown case
      // should keep narrowing this table same as every other cross-filtered one.
      const baseParams = currentFilterParams();
      const params = { ...baseParams, limeiq_key: umrRowFlagFilter.value ? undefined : baseParams.limeiq_key };

      loadingUmrs.value = true;
      try {
        const umrsRes = await BdxValidationService.getUmrs({
          ...params,
          flag: filters.flag.length ? filters.flag : undefined,
          severity: filters.severity.length ? filters.severity : undefined,
          cancelToken,
        });
        umrsList.value = (umrsRes.data && umrsRes.data.umrs) || [];
      } catch (err) {
        if (axios.isCancel(err)) return;
        loadError.value = (err.response && err.response.data && err.response.data.detail) || 'Failed to load UMRs.';
      } finally {
        loadingUmrs.value = false;
      }
    }

    const UMR_COLUMNS = [
      { key: 'limeiq_key', label: 'LimeIQ Key' },
      { key: 'report_period', label: 'Current Report Period' },
      { key: 'critical_count', label: 'Critical' },
      { key: 'non_critical_count', label: 'Non-Critical' },
    ];

    // Per-column search + sort for the bell icon's UMR table - same UI/interaction as
    // RecProductionReport.vue's own Variance Transaction Details table (RecColumnSearchMenu +
    // toggleColumnSort's up/down carets), but BOTH stay entirely client-side here (unlike that
    // table, or this report's own Claim Details table below) since GET /umrs is never paginated -
    // umrsList already holds every row matching the current report filters, so there's no
    // "only sorts/searches what's been loaded so far" caveat to work around.
    const umrColumnSearch = ref({});
    const umrColumnSearchMenuRef = ref(null);
    function setUmrColumnSearch(key, text) {
      umrColumnSearch.value = { ...umrColumnSearch.value, [key]: text };
    }

    const umrSortBy = ref(null);
    const umrSortDesc = ref(false);
    function toggleUmrColumnSort(key) {
      if (umrSortBy.value === key) {
        umrSortDesc.value = !umrSortDesc.value;
      } else {
        umrSortBy.value = key;
        umrSortDesc.value = false;
      }
    }

    // Report Period's search matches the formatted label the user actually sees ("April 2026"),
    // not the raw "YYYY-MM" row value it's searching against every other column - unlike
    // RecProductionReport's own column search (which always matches each row's raw value), a
    // literal "2026-04" substring match would silently never match typing "april".
    function umrColumnSearchValue(row, key) {
      if ('report_period' === key) return row.report_period ? formatReportPeriodLabel(row.report_period) : '';
      return row[key];
    }

    const displayedUmrsList = computed(() => {
      const rows = umrsList.value.filter((row) =>
        UMR_COLUMNS.every((col) => {
          const stored = umrColumnSearch.value[col.key];
          const rawValue = umrColumnSearchValue(row, col.key);
          // LIQ-2304: "Blank only" (see RecColumnSearchMenu's own allowBlank/BLANK_FILTER_VALUE) -
          // a dedicated equality check against the sentinel, checked BEFORE the normal
          // substring-match path below (which would otherwise just lowercase/trim it like any
          // other typed text and never match anything, since no real value equals that sentinel).
          if (BLANK_FILTER_VALUE === stored) {
            return null === rawValue || undefined === rawValue || '' === String(rawValue).trim();
          }
          const query = (stored || '').trim().toLowerCase();
          if (!query) return true;
          const stringValue = null === rawValue || undefined === rawValue ? '' : String(rawValue);
          return stringValue.toLowerCase().includes(query);
        })
      );
      if (!umrSortBy.value) return rows;

      const key = umrSortBy.value;
      const dir = umrSortDesc.value ? -1 : 1;
      return [...rows].sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        if (av === bv) return 0;
        if (null === av || undefined === av) return 1;
        if (null === bv || undefined === bv) return -1;
        return av > bv ? dir : -dir;
      });
    });

    // PAGINATION: same "Load more" pattern as RecProductionReport.vue's own
    // Variance Transaction Details table (see its VARIANCE_INITIAL_PAGE_SIZE/
    // VARIANCE_LOAD_MORE_SIZE comment) - fetching all matching claims in one
    // shot (previously page_size: 500) was the reported slowness (824 KB /
    // ~5.7s for one page). Unlike that table's offset-based endpoint, /report
    // is page-number-based (page/page_size, page_size capped at 500 server-
    // side - see router.py), so REPORT_PAGE_SIZE stays constant across both
    // the initial fetch and every "Load more" click; only `page` increments.
    //
    // Claim Values & Dates and Additional Claim Details are now TWO
    // independently-paginated resources (GET /report vs. GET
    // /additional-claim-details - see schemas.ReportResponse's own docstring
    // on the backend), not one shared page/filter scope like before: a claim
    // can be filed against multiple UMRs/programs, and /report deliberately
    // deduplicates to one row per claim_reference (confirmed against the live
    // Power BI model - its own "Claim Values & Dates" visual does the same),
    // while /additional-claim-details keeps the un-deduplicated per-(claim,
    // UMR) grain since it displays UMR as its own column. Each table gets
    // its own "Load more" button/pagination state below as a result.
    const REPORT_PAGE_SIZE = 100;

    const reportPage = ref(1);
    const reportTotalRows = ref(0);
    const loadingReportInitial = ref(false);
    const loadingReportMore = ref(false);
    let reportCancelSource = null;
    let reportRequestId = 0;

    async function fetchReport({ append } = {}) {
      loadError.value = '';
      const requestId = ++reportRequestId;
      if (reportCancelSource) reportCancelSource.cancel();
      reportCancelSource = axios.CancelToken.source();
      const cancelToken = reportCancelSource.token;

      const page = append ? reportPage.value + 1 : 1;

      if (append) loadingReportMore.value = true;
      else loadingReportInitial.value = true;

      try {
        const params = currentFilterParams();
        const reportRes = await BdxValidationService.getReport({
          ...params,
          flag: filters.flag.length ? filters.flag : undefined,
          severity: filters.severity.length ? filters.severity : undefined,
          has_flag: umrRowFlagFilter.value || undefined,
          page,
          page_size: REPORT_PAGE_SIZE,
          cancelToken,
        });
        if (requestId !== reportRequestId) return;

        const reportData = reportRes.data || {};
        // claim_values/claim_details now share the same one-row-per-claim
        // grain and page/row order (both come from the one, now-deduplicated
        // /report response), so a positional merge is correct - unlike
        // before this endpoint split, claim_reference genuinely is unique
        // per row here now.
        const claimDetailsRows = reportData.claim_details || [];
        const newMergedRows = (reportData.claim_values || []).map((valuesRow, index) => ({
          ...valuesRow,
          ...claimDetailsRows[index],
        }));

        mergedClaimRowsList.value = append ? [...mergedClaimRowsList.value, ...newMergedRows] : newMergedRows;
        reportTotalRows.value = reportData.total_rows || 0;
        reportPage.value = page;
      } catch (err) {
        if (axios.isCancel(err) || requestId !== reportRequestId) return;
        loadError.value =
          (err.response && err.response.data && err.response.data.detail) || 'Failed to load report data.';
      } finally {
        if (requestId === reportRequestId) {
          loadingReportInitial.value = false;
          loadingReportMore.value = false;
        }
      }
    }

    const loadMoreReport = () => fetchReport({ append: true });
    const hasMoreReport = computed(() => mergedClaimRowsList.value.length < reportTotalRows.value);
    const reportLoadMoreCount = computed(() =>
      Math.min(REPORT_PAGE_SIZE, reportTotalRows.value - mergedClaimRowsList.value.length)
    );

    const ADDITIONAL_PAGE_SIZE = 100;

    const additionalPage = ref(1);
    const additionalTotalRows = ref(0);
    const loadingAdditionalInitial = ref(false);
    const loadingAdditionalMore = ref(false);
    let additionalCancelSource = null;
    let additionalRequestId = 0;

    // Sort is server-side (unlike the UMR table's own client-side sort above) - this table is
    // paginated via "Load more", so sorting only what's currently loaded would resort oddly as
    // more rows load in. GET /additional-claim-details' own sort_by/sort_dir reorder the FULL
    // filtered dataset, folded into its own pagination (see router.py's own comment on why that
    // needed its own cache key). Search stays client-side, same as
    // RecProductionReport.vue's own Variance Transaction Details table - narrowing only what's
    // already been loaded (see displayedAdditionalClaimDetailsRows below) rather than promoting it
    // to a real per-column server-side search too.
    const additionalSortBy = ref(null);
    const additionalSortDesc = ref(false);
    function toggleAdditionalColumnSort(key) {
      if (additionalSortBy.value === key) {
        additionalSortDesc.value = !additionalSortDesc.value;
      } else {
        additionalSortBy.value = key;
        additionalSortDesc.value = false;
      }
    }

    // Applicable Validation Columns' own sort - GET /report has no sort_by param (unlike GET
    // /additional-claim-details), so this stays entirely client-side over whatever's already
    // loaded (displayedMergedRows below), same "only sorts what's been loaded so far" caveat
    // search already has on this table (and the exact pattern displayedUmrsList's own umrSortBy/
    // umrSortDesc already use, since /umrs is unpaginated but client-side sort either way).
    const mergedSortBy = ref(null);
    const mergedSortDesc = ref(false);
    function toggleMergedColumnSort(key) {
      if (mergedSortBy.value === key) {
        mergedSortDesc.value = !mergedSortDesc.value;
      } else {
        mergedSortBy.value = key;
        mergedSortDesc.value = false;
      }
    }

    async function fetchAdditionalClaimDetails({ append } = {}) {
      loadError.value = '';
      const requestId = ++additionalRequestId;
      if (additionalCancelSource) additionalCancelSource.cancel();
      additionalCancelSource = axios.CancelToken.source();
      const cancelToken = additionalCancelSource.token;

      const page = append ? additionalPage.value + 1 : 1;

      if (append) loadingAdditionalMore.value = true;
      else loadingAdditionalInitial.value = true;

      try {
        const params = currentFilterParams();
        const additionalRes = await BdxValidationService.getAdditionalClaimDetails({
          ...params,
          flag: filters.flag.length ? filters.flag : undefined,
          severity: filters.severity.length ? filters.severity : undefined,
          has_flag: umrRowFlagFilter.value || undefined,
          sort_by: additionalSortBy.value || undefined,
          sort_dir: additionalSortDesc.value ? 'desc' : undefined,
          page,
          page_size: ADDITIONAL_PAGE_SIZE,
          cancelToken,
        });
        if (requestId !== additionalRequestId) return;

        const additionalData = additionalRes.data || {};
        const newAdditionalRows = additionalData.additional_claim_details || [];

        additionalClaimDetailsRowsList.value = append
          ? [...additionalClaimDetailsRowsList.value, ...newAdditionalRows]
          : newAdditionalRows;
        additionalTotalRows.value = additionalData.total_rows || 0;
        additionalPage.value = page;
      } catch (err) {
        if (axios.isCancel(err) || requestId !== additionalRequestId) return;
        loadError.value =
          (err.response && err.response.data && err.response.data.detail) ||
          'Failed to load additional claim details.';
      } finally {
        if (requestId === additionalRequestId) {
          loadingAdditionalInitial.value = false;
          loadingAdditionalMore.value = false;
        }
      }
    }

    const loadMoreAdditional = () => fetchAdditionalClaimDetails({ append: true });
    const hasMoreAdditional = computed(
      () => additionalClaimDetailsRowsList.value.length < additionalTotalRows.value
    );
    const additionalLoadMoreCount = computed(() =>
      Math.min(ADDITIONAL_PAGE_SIZE, additionalTotalRows.value - additionalClaimDetailsRowsList.value.length)
    );

    const additionalColumnSearch = ref({});
    const additionalColumnSearchMenuRef = ref(null);
    function setAdditionalColumnSearch(key, text) {
      additionalColumnSearch.value = { ...additionalColumnSearch.value, [key]: text };
      // Narrowing displayedAdditionalClaimDetailsRows (auto-sized columns, not table-layout:
      // fixed) can shift every later column's on-screen width/position once the DOM updates -
      // reposition() re-aligns the still-open popover against its own trigger icon's new spot, or
      // this filter's own picker ends up visually drifting away from the column it was opened for
      // (see reposition's own comment in RecColumnSearchMenu.vue for the exact bug this fixes).
      nextTick(() => {
        if (additionalColumnSearchMenuRef.value) additionalColumnSearchMenuRef.value.reposition();
      });
    }

    function additionalColumnSearchType(key) {
      return ADDITIONAL_CLAIM_DETAILS_DATE_COLUMN_KEYS.has(key) ? 'date-range' : 'text';
    }

    // Applicable Validation Columns' own per-column search - same shape/behavior as
    // additionalColumnSearch above (allow-blank, date-range for date columns, generic text
    // otherwise), added by explicit request. Reuses DATE_COLUMN_KEYS (the module-level constant
    // already covering every date column across BOTH tables, including the ones - date_opened/
    // date_coverage_confirmed/date_claim_amount_agreed - that only exist here) rather than a new,
    // narrower set.
    const mergedColumnSearch = ref({});
    const mergedColumnSearchMenuRef = ref(null);
    function setMergedColumnSearch(key, text) {
      mergedColumnSearch.value = { ...mergedColumnSearch.value, [key]: text };
      nextTick(() => {
        if (mergedColumnSearchMenuRef.value) mergedColumnSearchMenuRef.value.reposition();
      });
    }
    function mergedColumnSearchType(key) {
      return DATE_COLUMN_KEYS.has(key) ? 'date-range' : 'text';
    }
    function isMergedColumnFiltered(key) {
      // claim_reference's own search icon now opens the shared top-level checklist (see
      // claimReferenceColumnMenuOpen) instead of this table's own free-text popover - reads
      // filters.claimReference directly, same as isAdditionalColumnFiltered's own claim_reference
      // case, rather than mergedColumnSearch (which nothing writes to for this key any more).
      if ('claim_reference' === key) return filters.claimReference.length > 0;
      return isColumnSearchValueActive(mergedColumnSearch.value[key]);
    }

    // Shared by isAdditionalColumnFiltered/hasAdditionalColumnFilters/
    // displayedAdditionalClaimDetailsRows below - a date-range column's own stored value is an
    // { from, to } object (RecColumnSearchMenu's own date-range mode - see onDateRangeInput
    // there), not the plain string every other column stores, so a bare truthiness check on it
    // (like `!!stored`) wrongly reads as "filtered" even when the user opened the popover and
    // typed nothing, or picked then cleared both dates (an object is always truthy, even
    // `{from: '', to: ''}`) - this checks the object's actual contents instead.
    function isColumnSearchValueActive(stored) {
      if (BLANK_FILTER_VALUE === stored) return true;
      if (stored && 'object' === typeof stored) return !!(stored.from || stored.to);
      return !!stored;
    }

    // Drives the filter-icon swap for a Claim Details column's own search toggle (see the
    // template) - true when EITHER that column's own per-column search popover has a value, OR
    // (for every column with a direct counterpart in the top filter row) that shared filter is
    // currently narrowing the report. claim_reference/limeiq_key/reporting_period all cross-filter
    // this table via filters.claimReference/filters.limeIQKey/selectedPeriods the same way the top
    // "Claim Reference"/"LimeIQ Key"/"Report Period" controls do, and tpa via filters.party's own
    // "TPA::..." entries (the Party dropdown's own composite PartyType::GroupName keys - see
    // _DCA_SELECTIONS_DESCRIPTION on the backend) - so a column reads as "filtered" regardless of
    // which UI control (this column's own icon, or the top one) was actually used to narrow it,
    // same reasoning claim_status's own icon already gets for free by reading the shared
    // claimStatus ref directly. reporting_period/tpa have no per-column search popover of their
    // own (reporting_period gets its own period-tree menu instead - see
    // toggleReportPeriodColumnMenu; tpa isn't independently searchable at all), so
    // additionalColumnSearch.value[key] is always undefined for those two - only the shared-filter
    // checks below ever apply to them.
    function isAdditionalColumnFiltered(key) {
      if (isColumnSearchValueActive(additionalColumnSearch.value[key])) return true;
      if ('claim_reference' === key && filters.claimReference.length) return true;
      if ('limeiq_key' === key && filters.limeIQKey.length) return true;
      if ('reporting_period' === key && selectedPeriods.value.length) return true;
      if ('tpa' === key && filters.party.some((p) => p.startsWith('TPA::'))) return true;
      if ('umr' === key && filters.umr.length) return true;
      return false;
    }

    // Reporting Period's own header menu - the SAME period-tree control as the sidebar's own
    // "Report Period" section (periodTree/isYearExpanded/toggleYearExpanded/isYearSelected/
    // isYearPartiallySelected/toggleYear/allPeriodsSelected/toggleAllPeriods/selectedPeriods, all
    // reused as-is below in the template rather than duplicated), just rendered as a portaled
    // popover triggered from this column's own search icon instead of always-visible in the
    // sidebar - both stay in sync automatically since they share selectedPeriods. Portal/manual-
    // position-calc/outside-click-close pattern matches FcpColumnSearchMenu's own reasoning (this
    // popover is triggered from inside .bdx-table-scroll, an overflow: auto container that would
    // otherwise clip a normally-positioned dropdown).
    const reportPeriodColumnMenuOpen = ref(false);
    const reportPeriodColumnMenuRef = ref(null);
    const reportPeriodColumnMenuStyle = reactive({ top: '0px', left: '0px' });
    let reportPeriodColumnMenuTriggerEl = null;

    function positionReportPeriodColumnMenu() {
      const trigger = reportPeriodColumnMenuTriggerEl;
      if (!trigger || !reportPeriodColumnMenuRef.value) return;

      const rect = trigger.getBoundingClientRect();
      const menuRect = reportPeriodColumnMenuRef.value.getBoundingClientRect();
      const margin = 8;

      let left = rect.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin);

      let top = rect.bottom + 2;
      if (top + menuRect.height + margin > window.innerHeight) {
        top = rect.top - menuRect.height - 2;
      }
      top = Math.max(margin, top);

      reportPeriodColumnMenuStyle.top = `${top}px`;
      reportPeriodColumnMenuStyle.left = `${left}px`;
    }

    function toggleReportPeriodColumnMenu(event) {
      if (reportPeriodColumnMenuOpen.value) {
        reportPeriodColumnMenuOpen.value = false;
        return;
      }
      reportPeriodColumnMenuTriggerEl = event.currentTarget;
      reportPeriodColumnMenuOpen.value = true;
      nextTick(positionReportPeriodColumnMenu);
    }

    function closeReportPeriodColumnMenu() {
      reportPeriodColumnMenuOpen.value = false;
    }

    function onReportPeriodColumnMenuMousedown(event) {
      if (!reportPeriodColumnMenuOpen.value) return;
      if (reportPeriodColumnMenuRef.value && reportPeriodColumnMenuRef.value.contains(event.target)) return;
      if (event.target.closest && event.target.closest('.bdx-th-report-period-toggle')) return;
      closeReportPeriodColumnMenu();
    }

    function onReportPeriodColumnMenuScroll() {
      if (reportPeriodColumnMenuOpen.value) closeReportPeriodColumnMenu();
    }

    function onReportPeriodColumnMenuKeydown(event) {
      if ('Escape' === event.key && reportPeriodColumnMenuOpen.value) closeReportPeriodColumnMenu();
    }

    // Checking/unchecking a period while this menu is open triggers a real refetch (selectedPeriods
    // is a top-level filter), which can reflow Claim Details' own auto-sized columns once the new
    // data lands - reposition (not close, unlike onReportPeriodColumnMenuScroll's own scroll
    // handling) so the still-open menu stays pinned under its own icon instead of drifting, same
    // fix/reasoning as FcpColumnSearchMenu's own reposition() for this table's other column
    // popovers.
    watch(additionalClaimDetailsRowsList, () => {
      if (reportPeriodColumnMenuOpen.value) nextTick(positionReportPeriodColumnMenu);
    });

    // Claim Reference column menu - same portal/position/outside-click pattern as
    // reportPeriodColumnMenuOpen/etc. above, reused verbatim rather than duplicated per-instance,
    // but for the top filter row's own Claim Reference checklist (filterDropdowns/
    // claimReferenceDropdownConfig/dropdownSearch/toggleFilterOption/toggleAllOptions/
    // filteredDropdownOptions/selectAllFilteredDropdownOptions - all reused as-is, not
    // reimplemented) instead of the period tree. Added by explicit request so Claim Details' AND
    // Applicable Validation Columns' own Claim Reference column search icon opens the SAME
    // checklist the top filter row's own dropdown does - real, server-side, full-dataset
    // filtering via filters.claimReference, rather than each table's own client-side-only,
    // loaded-rows-only free-text search. A single shared instance (like reportPeriodColumnMenuOpen)
    // is enough even though it can now be triggered from either table's own column header, since
    // only one can be open at a time either way.
    const claimReferenceColumnMenuOpen = ref(false);
    const claimReferenceColumnMenuRef = ref(null);
    const claimReferenceColumnMenuStyle = reactive({ top: '0px', left: '0px' });
    let claimReferenceColumnMenuTriggerEl = null;
    // Snapshot of the trigger icon's own position, refreshed every time positionClaim
    // ReferenceColumnMenu runs (both the initial open and the reflow-watcher's reposition below) -
    // onClaimReferenceColumnMenuScroll compares against this to tell "the trigger itself moved"
    // (page/table scroll - close) apart from "the menu's own checklist scrolled" (a real, expected
    // interaction here, unlike the period tree, which rarely needs its own internal scrollbar -
    // see that handler's own comment for the reported bug this fixes).
    let claimReferenceColumnMenuAnchorRect = null;

    function positionClaimReferenceColumnMenu() {
      const trigger = claimReferenceColumnMenuTriggerEl;
      if (!trigger || !claimReferenceColumnMenuRef.value) return;

      const rect = trigger.getBoundingClientRect();
      claimReferenceColumnMenuAnchorRect = rect;
      const menuRect = claimReferenceColumnMenuRef.value.getBoundingClientRect();
      const margin = 8;

      let left = rect.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin);

      let top = rect.bottom + 2;
      if (top + menuRect.height + margin > window.innerHeight) {
        top = rect.top - menuRect.height - 2;
      }
      top = Math.max(margin, top);

      claimReferenceColumnMenuStyle.top = `${top}px`;
      claimReferenceColumnMenuStyle.left = `${left}px`;
    }

    function toggleClaimReferenceColumnMenu(event) {
      if (claimReferenceColumnMenuOpen.value) {
        closeClaimReferenceColumnMenu();
        return;
      }
      claimReferenceColumnMenuTriggerEl = event.currentTarget;
      claimReferenceColumnMenuOpen.value = true;
      nextTick(positionClaimReferenceColumnMenu);
    }

    function closeClaimReferenceColumnMenu() {
      claimReferenceColumnMenuOpen.value = false;
      claimReferenceColumnMenuAnchorRect = null;
    }

    function onClaimReferenceColumnMenuMousedown(event) {
      if (!claimReferenceColumnMenuOpen.value) return;
      if (claimReferenceColumnMenuRef.value && claimReferenceColumnMenuRef.value.contains(event.target)) return;
      if (event.target.closest && event.target.closest('.bdx-th-claim-reference-toggle')) return;
      closeClaimReferenceColumnMenu();
    }

    // Reported bug: scrolling this menu's OWN checklist (its own overflow-y: auto - a real,
    // expected interaction, unlike reportPeriodColumnMenuOpen's own tree content, which rarely
    // needs one) fired a native scroll event that reached this window-level capture-phase
    // listener too, closing the menu the instant the user tried to scroll through a long claim
    // reference list. Fixed the same way RecColumnSearchMenu.vue's own onScroll already handles
    // the identical class of bug: only close on a scroll that PROVABLY moved the trigger icon
    // itself (invalidating the position snapshot), not just any scroll anywhere on the page.
    function onClaimReferenceColumnMenuScroll() {
      if (!claimReferenceColumnMenuOpen.value) return;
      const trigger = claimReferenceColumnMenuTriggerEl;
      const anchorRect = claimReferenceColumnMenuAnchorRect;
      if (!trigger || !anchorRect || !document.contains(trigger)) return;

      const rect = trigger.getBoundingClientRect();
      if (Math.abs(rect.left - anchorRect.left) < 1 && Math.abs(rect.bottom - anchorRect.bottom) < 1) {
        return;
      }
      closeClaimReferenceColumnMenu();
    }

    function onClaimReferenceColumnMenuKeydown(event) {
      if ('Escape' === event.key && claimReferenceColumnMenuOpen.value) closeClaimReferenceColumnMenu();
    }

    // Checking a claim reference triggers a real refetch (same reasoning as
    // reportPeriodColumnMenuOpen's own watch above) - covers BOTH tables this menu can now be
    // triggered from, since either one's own reflow could shift the trigger this menu is pinned to.
    watch([additionalClaimDetailsRowsList, mergedClaimRowsList], () => {
      if (claimReferenceColumnMenuOpen.value) nextTick(positionClaimReferenceColumnMenu);
    });

    // TPA column menu (Claim Details only - Applicable Validation Columns has no tpa column) -
    // same portal/position/outside-click/scroll pattern as claimReferenceColumnMenuOpen above, but
    // for the Party dropdown's own TPA section instead of the top filter row's Claim Reference
    // checklist, added by explicit request ("we'll be getting the list of TPAs from the party
    // filter - add that as a dropdown for the TPA column"). tpaGroup/isPartyTypeSelected/
    // isPartyTypePartiallySelected/togglePartyType/isPartyNameSelected/togglePartyName are all
    // reused as-is (not reimplemented) - checking a TPA here writes the exact same "TPA::Name"
    // key into filters.party the Party dropdown's own TPA section already does, so both stay in
    // sync automatically and isAdditionalColumnFiltered's own existing tpa case (checking
    // filters.party.some(p => p.startsWith('TPA::'))) already lights up this column's icon
    // correctly with no changes needed there.
    const tpaGroup = computed(
      () => partyGroupsList.value.find((g) => 'TPA' === g.partyType) || { partyType: 'TPA', names: [] }
    );
    const tpaColumnMenuSearch = ref('');
    const filteredTpaColumnMenuNames = computed(() => {
      const query = tpaColumnMenuSearch.value.trim().toLowerCase();
      if (!query) return tpaGroup.value.names;
      return tpaGroup.value.names.filter((name) => name.toLowerCase().includes(query));
    });
    // Same "select all currently displayed/filtered options" Enter handler as
    // selectAllFilteredPartyGroups/selectAllFilteredDropdownOptions elsewhere.
    function selectAllFilteredTpaColumnMenuNames() {
      const keys = filteredTpaColumnMenuNames.value.map((name) => partyKey('TPA', name));
      if (!keys.length) return;
      filters.party = Array.from(new Set([...filters.party, ...keys]));
    }

    const tpaColumnMenuOpen = ref(false);
    const tpaColumnMenuRef = ref(null);
    const tpaColumnMenuStyle = reactive({ top: '0px', left: '0px' });
    let tpaColumnMenuTriggerEl = null;
    let tpaColumnMenuAnchorRect = null;

    function positionTpaColumnMenu() {
      const trigger = tpaColumnMenuTriggerEl;
      if (!trigger || !tpaColumnMenuRef.value) return;

      const rect = trigger.getBoundingClientRect();
      tpaColumnMenuAnchorRect = rect;
      const menuRect = tpaColumnMenuRef.value.getBoundingClientRect();
      const margin = 8;

      let left = rect.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin);

      let top = rect.bottom + 2;
      if (top + menuRect.height + margin > window.innerHeight) {
        top = rect.top - menuRect.height - 2;
      }
      top = Math.max(margin, top);

      tpaColumnMenuStyle.top = `${top}px`;
      tpaColumnMenuStyle.left = `${left}px`;
    }

    function toggleTpaColumnMenu(event) {
      if (tpaColumnMenuOpen.value) {
        closeTpaColumnMenu();
        return;
      }
      tpaColumnMenuTriggerEl = event.currentTarget;
      tpaColumnMenuOpen.value = true;
      nextTick(positionTpaColumnMenu);
    }

    function closeTpaColumnMenu() {
      tpaColumnMenuOpen.value = false;
      tpaColumnMenuAnchorRect = null;
    }

    function onTpaColumnMenuMousedown(event) {
      if (!tpaColumnMenuOpen.value) return;
      if (tpaColumnMenuRef.value && tpaColumnMenuRef.value.contains(event.target)) return;
      if (event.target.closest && event.target.closest('.bdx-th-tpa-toggle')) return;
      closeTpaColumnMenu();
    }

    // Same "only close on a scroll that provably moved the trigger" fix as
    // onClaimReferenceColumnMenuScroll above - this menu's own checklist can scroll too.
    function onTpaColumnMenuScroll() {
      if (!tpaColumnMenuOpen.value) return;
      const trigger = tpaColumnMenuTriggerEl;
      const anchorRect = tpaColumnMenuAnchorRect;
      if (!trigger || !anchorRect || !document.contains(trigger)) return;

      const rect = trigger.getBoundingClientRect();
      if (Math.abs(rect.left - anchorRect.left) < 1 && Math.abs(rect.bottom - anchorRect.bottom) < 1) {
        return;
      }
      closeTpaColumnMenu();
    }

    function onTpaColumnMenuKeydown(event) {
      if ('Escape' === event.key && tpaColumnMenuOpen.value) closeTpaColumnMenu();
    }

    watch(additionalClaimDetailsRowsList, () => {
      if (tpaColumnMenuOpen.value) nextTick(positionTpaColumnMenu);
    });

    // UMR column menu (Claim Details only - Applicable Validation Columns has no umr column) -
    // same portal/position/outside-click/scroll pattern as claimReferenceColumnMenuOpen above, but
    // for the dedicated filters.umr/umrOptionsList cross-filter added by explicit request ("not
    // limeiq keys, I want actual UMRs as a dropdown from UMR column in claim details table" -
    // "Full cross-filter (Recommended)"), reusing umrDropdownConfig/dropdownSearch.umr/
    // toggleFilterOption/toggleAllOptions/filteredDropdownOptions/selectAllFilteredDropdownOptions/
    // dropdownOptionsTruncatedCount exactly as claimReferenceColumnMenuOpen's own template block
    // does, just with its own independent open/position state (like tpaColumnMenuOpen's own,
    // separate from claimReferenceColumnMenuOpen's).
    const umrColumnMenuOpen = ref(false);
    const umrColumnMenuRef = ref(null);
    const umrColumnMenuStyle = reactive({ top: '0px', left: '0px' });
    let umrColumnMenuTriggerEl = null;
    let umrColumnMenuAnchorRect = null;

    function positionUmrColumnMenu() {
      const trigger = umrColumnMenuTriggerEl;
      if (!trigger || !umrColumnMenuRef.value) return;

      const rect = trigger.getBoundingClientRect();
      umrColumnMenuAnchorRect = rect;
      const menuRect = umrColumnMenuRef.value.getBoundingClientRect();
      const margin = 8;

      let left = rect.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin);

      let top = rect.bottom + 2;
      if (top + menuRect.height + margin > window.innerHeight) {
        top = rect.top - menuRect.height - 2;
      }
      top = Math.max(margin, top);

      umrColumnMenuStyle.top = `${top}px`;
      umrColumnMenuStyle.left = `${left}px`;
    }

    function toggleUmrColumnMenu(event) {
      if (umrColumnMenuOpen.value) {
        closeUmrColumnMenu();
        return;
      }
      umrColumnMenuTriggerEl = event.currentTarget;
      umrColumnMenuOpen.value = true;
      nextTick(positionUmrColumnMenu);
    }

    function closeUmrColumnMenu() {
      umrColumnMenuOpen.value = false;
      umrColumnMenuAnchorRect = null;
    }

    function onUmrColumnMenuMousedown(event) {
      if (!umrColumnMenuOpen.value) return;
      if (umrColumnMenuRef.value && umrColumnMenuRef.value.contains(event.target)) return;
      if (event.target.closest && event.target.closest('.bdx-th-umr-toggle')) return;
      closeUmrColumnMenu();
    }

    // Same "only close on a scroll that provably moved the trigger" fix as
    // onClaimReferenceColumnMenuScroll above - this menu's own checklist can scroll too.
    function onUmrColumnMenuScroll() {
      if (!umrColumnMenuOpen.value) return;
      const trigger = umrColumnMenuTriggerEl;
      const anchorRect = umrColumnMenuAnchorRect;
      if (!trigger || !anchorRect || !document.contains(trigger)) return;

      const rect = trigger.getBoundingClientRect();
      if (Math.abs(rect.left - anchorRect.left) < 1 && Math.abs(rect.bottom - anchorRect.bottom) < 1) {
        return;
      }
      closeUmrColumnMenu();
    }

    function onUmrColumnMenuKeydown(event) {
      if ('Escape' === event.key && umrColumnMenuOpen.value) closeUmrColumnMenu();
    }

    watch(additionalClaimDetailsRowsList, () => {
      if (umrColumnMenuOpen.value) nextTick(positionUmrColumnMenu);
    });

    // LimeIQ Key column menu (Claim Details only - Applicable Validation Columns has no
    // limeiq_key column) - same portal/position/outside-click/scroll pattern as
    // claimReferenceColumnMenuOpen above, but reused for the top filter row's own LimeIQ Key
    // checklist instead (limeIQKeyDropdownConfig/dropdownSearch.limeIQKey/filters.limeIQKey/
    // toggleFilterOption/toggleAllOptions/filteredDropdownOptions/selectAllFilteredDropdownOptions/
    // dropdownOptionsTruncatedCount, all reused as-is - not reimplemented), added by explicit
    // request so this column's own search icon opens the SAME real, server-side, full-dataset
    // checklist the top filter row's own LimeIQ Key dropdown does, replacing the generic
    // client-side-only free-text popover every other column still gets. Unlike UMR's own
    // umrDropdownConfig, LimeIQ Key already has a top filter row entry (filterDropdowns' own
    // 'limeIQKey' key), so this looks it up via find() exactly like claimReferenceDropdownConfig
    // does, rather than building a standalone plain object.
    const limeIQKeyDropdownConfig = computed(() => filterDropdowns.value.find((dd) => 'limeIQKey' === dd.key));

    const limeIQKeyColumnMenuOpen = ref(false);
    const limeIQKeyColumnMenuRef = ref(null);
    const limeIQKeyColumnMenuStyle = reactive({ top: '0px', left: '0px' });
    let limeIQKeyColumnMenuTriggerEl = null;
    let limeIQKeyColumnMenuAnchorRect = null;

    function positionLimeIQKeyColumnMenu() {
      const trigger = limeIQKeyColumnMenuTriggerEl;
      if (!trigger || !limeIQKeyColumnMenuRef.value) return;

      const rect = trigger.getBoundingClientRect();
      limeIQKeyColumnMenuAnchorRect = rect;
      const menuRect = limeIQKeyColumnMenuRef.value.getBoundingClientRect();
      const margin = 8;

      // Anchored to the trigger's RIGHT edge, opening leftward - unlike every other column menu
      // above (which anchor to rect.left and open rightward), by explicit request: this column
      // sits at the right edge of Claim Details' own scrollable table, so opening rightward ran
      // this (now-widened) menu off toward the browser's own right edge instead of over the table.
      let left = rect.right - menuRect.width;
      left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin);

      let top = rect.bottom + 2;
      if (top + menuRect.height + margin > window.innerHeight) {
        top = rect.top - menuRect.height - 2;
      }
      top = Math.max(margin, top);

      limeIQKeyColumnMenuStyle.top = `${top}px`;
      limeIQKeyColumnMenuStyle.left = `${left}px`;
    }

    function toggleLimeIQKeyColumnMenu(event) {
      if (limeIQKeyColumnMenuOpen.value) {
        closeLimeIQKeyColumnMenu();
        return;
      }
      limeIQKeyColumnMenuTriggerEl = event.currentTarget;
      limeIQKeyColumnMenuOpen.value = true;
      nextTick(positionLimeIQKeyColumnMenu);
    }

    function closeLimeIQKeyColumnMenu() {
      limeIQKeyColumnMenuOpen.value = false;
      limeIQKeyColumnMenuAnchorRect = null;
    }

    function onLimeIQKeyColumnMenuMousedown(event) {
      if (!limeIQKeyColumnMenuOpen.value) return;
      if (limeIQKeyColumnMenuRef.value && limeIQKeyColumnMenuRef.value.contains(event.target)) return;
      if (event.target.closest && event.target.closest('.bdx-th-limeiq-key-toggle')) return;
      closeLimeIQKeyColumnMenu();
    }

    // Same "only close on a scroll that provably moved the trigger" fix as
    // onClaimReferenceColumnMenuScroll above - this menu's own checklist can scroll too.
    function onLimeIQKeyColumnMenuScroll() {
      if (!limeIQKeyColumnMenuOpen.value) return;
      const trigger = limeIQKeyColumnMenuTriggerEl;
      const anchorRect = limeIQKeyColumnMenuAnchorRect;
      if (!trigger || !anchorRect || !document.contains(trigger)) return;

      const rect = trigger.getBoundingClientRect();
      if (Math.abs(rect.left - anchorRect.left) < 1 && Math.abs(rect.bottom - anchorRect.bottom) < 1) {
        return;
      }
      closeLimeIQKeyColumnMenu();
    }

    function onLimeIQKeyColumnMenuKeydown(event) {
      if ('Escape' === event.key && limeIQKeyColumnMenuOpen.value) closeLimeIQKeyColumnMenu();
    }

    watch(additionalClaimDetailsRowsList, () => {
      if (limeIQKeyColumnMenuOpen.value) nextTick(positionLimeIQKeyColumnMenu);
    });

    // Column resize for Claim Details - same drag-a-divider mechanism as
    // RecProductionReport.vue's own columnWidths/columnStyle/startColumnResize (see its own
    // comments), reimplemented here rather than shared since it's small and reads/writes this
    // component's own additionalClaimDetailsColumns keys, not REC's variance/bdx/cashbook ones.
    //
    // Unlike RecProductionReport.vue's own columnWidths (which only seeds the couple of columns
    // whose values vary wildly in length, e.g. 'variance:claim' - everything else there starts
    // unconstrained, auto-sized to content), EVERY Claim Details column gets a starting width
    // here, per explicit request: with 26 columns of wildly differing natural content width (a
    // 2-letter Loss State next to a full Loss Description), auto-layout made the table's own
    // total width - and which columns needed horizontal scrolling to reach - unpredictable from
    // one filter/row set to the next. Still just STARTING widths, not caps -
    // startAdditionalColumnResize below overwrites the same key, so any column can still be
    // dragged narrower or wider from here, exactly like RecProductionReport.vue's own resize
    // handles.
    const ADDITIONAL_CLAIM_DETAILS_DEFAULT_COLUMN_WIDTH = 130;
    const ADDITIONAL_CLAIM_DETAILS_COLUMN_WIDTH_OVERRIDES = {
      reporting_period: 100,
      tpa: 120,
      claim_reference: 110,
      claim_status: 90,
      insured: 170,
      loss_state: 80,
      umr: 190,
      risk_code: 80,
      binder_contract_inception: 110,
      binder_contract_expiry: 110,
      risk_inception_date: 110,
      risk_expiry_date: 110,
      date_of_loss: 100,
      date_claim_made: 110,
      date_claim_opened: 110,
      date_closed: 100,
      class_of_business: 140,
      policy_or_group_ref: 130,
      denial: 70,
      reason_for_denial: 160,
      refer_to_uw: 120,
      currency: 80,
      cause_of_loss: 130,
      loss_description: 260,
      cat_code: 80,
      catastrophe_name: 140,
      limeiq_key: 200,
    };
    const additionalColumnWidths = ref(
      Object.fromEntries(
        ADDITIONAL_CLAIM_DETAILS_COLUMNS.map((col) => [
          `additional:${col.key}`,
          ADDITIONAL_CLAIM_DETAILS_COLUMN_WIDTH_OVERRIDES[col.key] || ADDITIONAL_CLAIM_DETAILS_DEFAULT_COLUMN_WIDTH,
        ])
      )
    );

    function additionalColumnStyle(widthKey) {
      const width = additionalColumnWidths.value[widthKey];
      // Every Claim Details column now has a width in additionalColumnWidths (see its own
      // comment), so this always clips/ellipsizes in practice - the `width` guard just keeps this
      // function safe/generic if a caller ever asks about a widthKey that isn't in that map at
      // all. Applied to both the header and body's own wrapping div (see the template) - CSS auto
      // table-layout sizes a column to its widest cell regardless of which cell asked for a
      // narrower width, so constraining only the header can't actually shrink the column while a
      // wider, unconstrained body cell is still in play.
      return width
        ? { width: `${width}px`, maxWidth: `${width}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        : {};
    }

    function startAdditionalColumnResize(event, widthKey) {
      event.preventDefault();

      const th = event.target.closest('th');
      const startX = event.clientX;
      const startWidth = th ? th.offsetWidth : 100;

      function onMouseMove(moveEvent) {
        additionalColumnWidths.value = {
          ...additionalColumnWidths.value,
          [widthKey]: Math.max(40, startWidth + (moveEvent.clientX - startX)),
        };
      }

      function onMouseUp() {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      }

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    // Applicable Validation Columns' own fixed/drag-resizable widths - same mechanism as
    // additionalColumnWidths/additionalColumnStyle/startAdditionalColumnResize above, just keyed
    // `merged:${key}` instead of `additional:${key}` so the two tables' own widths never collide
    // in the same map (mergedColumnWidths is its own separate ref, not a shared one, but the
    // prefix convention is kept identical for consistency). Seeded for every MERGED_COLUMNS entry
    // (not just visibleMergedColumns' current subset), same reasoning as the other table's own
    // seed - a column hidden by the current flag's mapping still has a real width ready for when
    // it's shown again, rather than needing a fresh drag every time visibility changes.
    const MERGED_DEFAULT_COLUMN_WIDTH = 130;
    const MERGED_COLUMN_WIDTH_OVERRIDES = {
      claim_reference: 110,
      claim_status: 90,
      denial: 70,
      refer_to_uw: 110,
      variance_total_incurred_mismatch: 150,
      variance_incurred_indemnity_mismatch: 150,
      variance_incurred_fees_mismatch: 150,
      total_incurred: 110,
      incurred_indemnity: 120,
      incurred_fees: 100,
      total_reserve: 110,
      total_paid_this_month: 140,
      paid_this_month_indemnity: 150,
      paid_this_month_fees: 130,
      previously_paid_indemnity: 150,
      previously_paid_fees: 130,
      reserve_indemnity: 120,
      reserve_fees: 100,
      reserve_expenses: 110,
      reserve_tpa_fees: 110,
      reserve_defence_fees: 130,
      reserve_attorney_coverage_fees: 170,
      reserve_adjuster_fees: 130,
      risk_expiry_date: 100,
      risk_inception_date: 100,
      date_of_loss: 100,
      date_claim_made: 110,
      date_opened: 100,
      date_coverage_confirmed: 130,
      date_claim_amount_agreed: 140,
      date_closed: 100,
      cat_code: 80,
      catastrophe_name: 140,
      policy_or_group_ref: 130,
      loss_state: 80,
      cause_of_loss: 130,
      risk_code: 80,
      class_of_business: 140,
      loss_description: 220,
      currency: 80,
      reason_for_denial: 160,
      binder_contract_expiry: 110,
      state_of_filing: 110,
      certificate_reference: 140,
      binder_contract_inception: 110,
    };
    const mergedColumnWidths = ref(
      Object.fromEntries(
        MERGED_COLUMNS.map((col) => [
          `merged:${col.key}`,
          MERGED_COLUMN_WIDTH_OVERRIDES[col.key] || MERGED_DEFAULT_COLUMN_WIDTH,
        ])
      )
    );

    function mergedColumnStyle(widthKey) {
      const width = mergedColumnWidths.value[widthKey];
      return width
        ? { width: `${width}px`, maxWidth: `${width}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        : {};
    }

    function startMergedColumnResize(event, widthKey) {
      event.preventDefault();

      const th = event.target.closest('th');
      const startX = event.clientX;
      const startWidth = th ? th.offsetWidth : 100;

      function onMouseMove(moveEvent) {
        mergedColumnWidths.value = {
          ...mergedColumnWidths.value,
          [widthKey]: Math.max(40, startWidth + (moveEvent.clientX - startX)),
        };
      }

      function onMouseUp() {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      }

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    // Claim Status's own header menu (see the template) - a single shared RecInlineOptionsMenu
    // instance, same as RecProductionReport.vue's own Source/Variance Type/Admin Approval cells
    // all reuse one inlineOptionsMenuRef rather than one popover per trigger.
    const inlineOptionsMenuRef = ref(null);
    const claimStatusMenuOptions = [
      { value: 'all', label: 'All' },
      { value: 'open', label: 'Open' },
      { value: 'closed', label: 'Closed' },
      { value: 'blank', label: 'Blank' },
    ];
    function setClaimStatusFilter(value) {
      claimStatus.value = value;
    }

    // Narrows only what's already been loaded (additionalClaimDetailsRowsList, itself already
    // server-sorted - see fetchAdditionalClaimDetails) - "Showing N of M rows"/hasMoreAdditional
    // above deliberately keep counting the RAW loaded rows, not this filtered view, same
    // reasoning RecProductionReport.vue's own displayedVarianceRows/varianceDetailRows split has.
    const displayedAdditionalClaimDetailsRows = computed(() =>
      additionalClaimDetailsRowsList.value.filter((row) =>
        ADDITIONAL_CLAIM_DETAILS_COLUMNS.every((col) => {
          const stored = additionalColumnSearch.value[col.key];
          const rawValue = row[col.key];
          // LIQ-2304: "Blank only" - see displayedUmrsList's own identical comment for why this
          // needs to be a dedicated check ahead of the normal substring-match path below.
          if (BLANK_FILTER_VALUE === stored) {
            return null === rawValue || undefined === rawValue || '' === String(rawValue).trim();
          }
          // Date-range mode (RecColumnSearchMenu's own date-range type - see
          // additionalColumnSearchType/ADDITIONAL_CLAIM_DETAILS_DATE_COLUMN_KEYS): stored is an
          // { from, to } pair of "YYYY-MM-DD" strings rather than the plain substring query every
          // other column uses below, so it needs real chronological comparison, not
          // includes(). ISO "YYYY-MM-DD" strings sort lexicographically in the same order as the
          // dates themselves, so plain string <=/>= comparison is enough without parsing into real
          // Date objects. rawValue is taken from the API as-is (a date or datetime string) - only
          // its own leading 10 characters ("YYYY-MM-DD") are compared, so a datetime's time
          // component never throws off the match.
          if (ADDITIONAL_CLAIM_DETAILS_DATE_COLUMN_KEYS.has(col.key)) {
            const range = stored && 'object' === typeof stored ? stored : {};
            const from = (range.from || '').trim();
            const to = (range.to || '').trim();
            if (!from && !to) return true;
            const rawDate = rawValue ? String(rawValue).slice(0, 10) : '';
            if (!rawDate) return false;
            if (from && rawDate < from) return false;
            if (to && rawDate > to) return false;
            return true;
          }
          const query = (stored || '').trim().toLowerCase();
          if (!query) return true;
          const stringValue = null === rawValue || undefined === rawValue ? '' : String(rawValue);
          return stringValue.toLowerCase().includes(query);
        })
      )
    );

    // Applicable Validation Columns' own filter+sort, same "only what's already been loaded"
    // caveat as displayedAdditionalClaimDetailsRows above (mergedClaimRowsList is itself
    // paginated via fetchReport/loadMoreReport). Filtered against visibleMergedColumns (not the
    // full, fixed MERGED_COLUMNS) so a column currently hidden by the active flag's own
    // value/detail-param mapping never silently keeps narrowing rows by a stale search typed
    // before that flag was selected. Text search matches formatCell's own FORMATTED value
    // ("1,234.56", "08-22-2025"), not the raw API value - the cells themselves render through
    // formatCell (see the template), so this matches what the user actually reads on screen and
    // types to search for, unlike Additional Claim Details' plain-text cells above (raw value
    // already IS the displayed value there).
    const displayedMergedRows = computed(() => {
      const rows = mergedClaimRowsList.value.filter((row) =>
        visibleMergedColumns.value.every((col) => {
          const stored = mergedColumnSearch.value[col.key];
          const rawValue = row[col.key];
          if (BLANK_FILTER_VALUE === stored) {
            return null === rawValue || undefined === rawValue || '' === String(rawValue).trim();
          }
          if (DATE_COLUMN_KEYS.has(col.key)) {
            const range = stored && 'object' === typeof stored ? stored : {};
            const from = (range.from || '').trim();
            const to = (range.to || '').trim();
            if (!from && !to) return true;
            const rawDate = rawValue ? String(rawValue).slice(0, 10) : '';
            if (!rawDate) return false;
            if (from && rawDate < from) return false;
            if (to && rawDate > to) return false;
            return true;
          }
          const query = (stored || '').trim().toLowerCase();
          if (!query) return true;
          const formatted = formatCell(rawValue, col.key);
          const stringValue = null === formatted || undefined === formatted ? '' : String(formatted);
          return stringValue.toLowerCase().includes(query);
        })
      );
      if (!mergedSortBy.value) return rows;

      const key = mergedSortBy.value;
      const dir = mergedSortDesc.value ? -1 : 1;
      return [...rows].sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        if (av === bv) return 0;
        if (null === av || undefined === av) return 1;
        if (null === bv || undefined === bv) return -1;
        return av > bv ? dir : -dir;
      });
    });

    function fetchFilteredData() {
      fetchFlagsAndKpis();
      fetchUmrs();
      fetchReport({ append: false });
      fetchAdditionalClaimDetails({ append: false });
    }

    onMounted(() => {
      fetchFlagColumnMap();
      fetchFilterOptions();
      fetchFilteredData();
    });

    // severity/flag/claimReference/limeIQKey/party are all multi-select
    // checklists (arrays) - an empty array means "All" (no filter applied),
    // matching the Report Period checkboxes' own convention.
    const filters = reactive({
      severity: parseArrayQueryParam(initialQuery.severity),
      flag: parseArrayQueryParam(initialQuery.flag),
      claimReference: parseArrayQueryParam(initialQuery.claim_reference),
      limeIQKey: parseArrayQueryParam(initialQuery.limeiq_key),
      umr: parseArrayQueryParam(initialQuery.umr),
      party: parseArrayQueryParam(initialQuery.party),
    });

    // Set only by selectUmrRow (clicking a row in the bell icon's UMR table) - unlike every other
    // filter above, NOT one of the Flag/Severity dropdowns' own state, so it can add its own "only
    // this program's FLAGGED claims" restriction on top of filters.limeIQKey without disturbing
    // whatever those dropdowns are separately set to (see GET /report's own has_flag param). Reset
    // to false by every OTHER path that changes filters.limeIQKey (the LimeIQ Key dropdown's own
    // toggleFilterOption/toggleAllOptions, and clearFilters) so it never silently outlives the
    // specific row-click gesture that set it.
    const umrRowFlagFilter = ref('true' === initialQuery.has_flag);

    // Every filter-affecting piece of state cross-filters every other one
    // (see the memory decision this replaces: "hardcoded for now, backend
    // coming soon needs this cross functionality") - fully, not just results:
    // bdxStatus/claimStatus/selectedPeriods/claimReference/limeIQKey/party
    // each also refresh the OTHER dropdowns' own OPTION lists (Report
    // Period/Party/Claim Reference/LimeIQ Key), same slicer-style cross-
    // filtering PBI's own filter pane does - see fetchFilterOptions' own
    // comment for the self-exclusion rule that keeps this from collapsing
    // multi-select lists down to just the current selection. severity/flag
    // are excluded from that reference refresh (fetchFilterOptions' two
    // backend endpoints don't accept them - same reasoning /flags and /kpis
    // don't either) but still refetch results via fetchFilteredData. Both
    // debounced (same 400ms as RecProductionReport.vue's own
    // fetchReportDebounced) so a burst of checkbox clicks - e.g. "Select all"
    // on a long list - fires one request instead of one per click.
    //
    // Declared AFTER `filters` above deliberately: watch()'s underlying Vue
    // Watcher evaluates its getter synchronously and immediately (even
    // without {immediate: true}), so a watch() call referencing `filters`
    // placed BEFORE this declaration would close over `filters` while it's
    // still undefined (var-hoisted by the build's Babel target, so this fails
    // silently as a TypeError deep inside Vue's own traverse(), not a
    // same-line ReferenceError) - which is exactly what broke the merged
    // table's rendering earlier (surfaced as "Maximum call stack size
    // exceeded", since the watcher's corrupted initial state cascaded into
    // Vue's render-time diffing).
    const fetchFilteredDataDebounced = debounce(fetchFilteredData, 400);
    const fetchFilterOptionsDebounced = debounce(fetchFilterOptions, 400);

    watch(
      [
        bdxStatus, claimStatus, selectedPeriods, () => filters.claimReference, () => filters.limeIQKey,
        () => filters.umr, () => filters.party,
      ],
      () => {
        fetchFilterOptionsDebounced();
        fetchFilteredDataDebounced();
      },
      { deep: true }
    );

    watch(
      [() => filters.severity, () => filters.flag],
      () => {
        // Now also re-narrows Party/TPA/Claim Reference/LimeIQ Key's own option lists (see
        // fetchFilterOptions' own comment) - previously omitted here entirely, since those two
        // endpoints didn't accept flag/severity at all until this same change added them.
        fetchFilterOptionsDebounced();
        fetchFilteredDataDebounced();
      },
      { deep: true }
    );

    // Claim Details' own column-header sort (see toggleAdditionalColumnSort) - server-side, so a
    // sort change needs a real refetch (from page 1, discarding whatever's been loaded via "Load
    // more" under the old order) rather than just a local re-render.
    watch([additionalSortBy, additionalSortDesc], () => fetchAdditionalClaimDetails({ append: false }));

    // Write side of the URL persistence seeded at the top of setup() (initialQuery) - same
    // router.replace (not push) pattern as RecProductionReport.vue's own urlQuery, so tweaking a
    // filter doesn't spam the browser's back button with one history entry per checkbox click.
    // Only omits a filter from the query when it's at its own default (bdxStatus defaults to
    // 'active', not 'all', unlike every other filter here - see its own ref() above).
    const urlQuery = computed(() => {
      const query = {};
      if (bdxStatus.value !== 'active') query.bdx_status = bdxStatus.value;
      if (claimStatus.value !== 'all') query.claim_status = claimStatus.value;
      if (selectedPeriods.value.length) query.report_periods = selectedPeriods.value;
      if (filters.severity.length) query.severity = filters.severity;
      if (filters.flag.length) query.flag = filters.flag;
      if (filters.claimReference.length) query.claim_reference = filters.claimReference;
      if (filters.limeIQKey.length) query.limeiq_key = filters.limeIQKey;
      if (filters.umr.length) query.umr = filters.umr;
      if (filters.party.length) query.party = filters.party;
      if (umrRowFlagFilter.value) query.has_flag = 'true';
      return query;
    });

    watch(urlQuery, (query) => {
      router.replace({ query }).catch(() => {});
    });

    const dropdownSearch = reactive({
      flag: '',
      claimReference: '',
      limeIQKey: '',
      umr: '',
    });

    // A computed, not a plain array, since every option list below now comes
    // from live-fetched state (GET /flags for flag names, GET /filter-options
    // for Claim Reference/LimeIQ Key).
    const filterDropdowns = computed(() => [
      {
        key: 'flag',
        label: 'Flag',
        // flagDropdownOptionsList, not criticalFlagsList/nonCriticalFlagsList - see that ref's own
        // comment for why the dropdown's own selectable options must stay self-excluded from the
        // current Flag/Severity selection, unlike the Critical/Non-Critical panels' own display.
        options: flagDropdownOptionsList.value,
        searchable: true,
        wide: true,
      },
      // Not wide - Party joining this row (see the template) left less room
      // to share, and Claim Reference is the one that gives it up: it's
      // still fully searchable/functional, just docked to .bdx-filter's
      // narrower default width instead of growing with the other three.
      { key: 'claimReference', label: 'Claim Reference', options: claimReferenceOptionsList.value, searchable: true, wide: false },
      { key: 'limeIQKey', label: 'LimeIQ Key', options: limeIQKeyOptionsList.value, searchable: true, wide: true },
    ]);

    // The Claim Reference COLUMN's own dd config, for the column-menu below - looked up by key
    // rather than duplicated, so it always reflects the exact same options/search state the top
    // filter row's own Claim Reference dropdown does (filterDropdowns' entries are keyed
    // 'claimReference', camelCase, matching filters.claimReference - NOT 'claim_reference', the
    // snake_case column key both tables use, hence the lookup rather than a direct index).
    const claimReferenceDropdownConfig = computed(() => filterDropdowns.value.find((dd) => 'claimReference' === dd.key));

    // UMR's own dd config, for the Claim Details column menu below - NOT part of filterDropdowns
    // above (unlike Claim Reference/LimeIQ Key), since the user only asked for a UMR dropdown on
    // the column itself, not a new entry in the top filter row - built as its own plain object
    // instead of a filterDropdowns find() for that reason, but still consumed by the exact same
    // generic filteredDropdownOptions/isAllSelected/toggleAllOptions/toggleFilterOption/
    // selectAllFilteredDropdownOptions/dropdownOptionsTruncatedCount helpers (all keyed off
    // dd.key/filters[dd.key]/dropdownSearch[dd.key], so no reimplementation needed).
    const umrDropdownConfig = computed(() => ({
      key: 'umr', label: 'UMR', options: umrOptionsList.value, searchable: true, wide: false,
    }));

    function filterSummary(key) {
      const selected = filters[key];
      if (selected.length === 0) return 'All';
      if (selected.length === 1) return selected[0];
      return `${selected.length} selected`;
    }

    // Claim Reference/LimeIQ Key back this dropdown with real, unbounded-size
    // API data now (unlike the old empty-placeholder static lists) - tens of
    // thousands of distinct values is normal for this field. b-dropdown-form's
    // slot content renders into the DOM regardless of open/closed state (not
    // lazy-mounted), so without a cap this v-for would try to build a vnode
    // array sized to the FULL list on every keystroke/filter change - Vue 2's
    // internal children-array normalization uses an .apply()/spread-style
    // call for performance, which hits the exact same V8 call-stack/argument-
    // count limit as the Math.max(...) bug above once the array is large
    // enough, throwing "Maximum call stack size exceeded". Capped here
    // instead - same reasoning as any real-world large-dataset combobox:
    // require typing to narrow down rather than ever rendering the whole list.
    const DROPDOWN_OPTIONS_RENDER_CAP = 200;

    function filteredDropdownOptions(dd) {
      const query = dropdownSearch[dd.key].trim().toLowerCase();
      const matches =
        !dd.searchable || !query ? dd.options : dd.options.filter((opt) => opt.toLowerCase().includes(query));
      return matches.slice(0, DROPDOWN_OPTIONS_RENDER_CAP);
    }

    function dropdownOptionsTruncatedCount(dd) {
      const query = dropdownSearch[dd.key].trim().toLowerCase();
      const totalMatches = (!dd.searchable || !query
        ? dd.options
        : dd.options.filter((opt) => opt.toLowerCase().includes(query))
      ).length;
      return Math.max(0, totalMatches - DROPDOWN_OPTIONS_RENDER_CAP);
    }

    function isAllSelected(dd) {
      return dd.options.length > 0 && filters[dd.key].length === dd.options.length;
    }

    function toggleAllOptions(dd) {
      if ('limeIQKey' === dd.key) umrRowFlagFilter.value = false;
      // Same reasoning as umrRowFlagFilter above, for filters.flag's own row-click counterpart -
      // see fetchFlagsAndKpis' own flagsParams comment for why the panels need this reset.
      if ('flag' === dd.key) selectedFlag.value = null;
      filters[dd.key] = isAllSelected(dd) ? [] : [...dd.options];
    }

    function toggleFilterOption(key, option) {
      if ('limeIQKey' === key) umrRowFlagFilter.value = false;
      if ('flag' === key) selectedFlag.value = null;
      filters[key] = filters[key].includes(option)
        ? filters[key].filter((o) => o !== option)
        : [...filters[key], option];
    }

    // Enter in this checklist's own search box (the b-dropdown-form's @submit.prevent - see that
    // fix's own comment for why Enter needs handling here at all, mirroring RecProductionReport.
    // vue's own selectAllFilteredVarianceTypes/etc.) selects every option the search currently
    // matches, rather than doing nothing - the FULL match set, not just filteredDropdownOptions'
    // own render-capped (DROPDOWN_OPTIONS_RENDER_CAP) slice, so a broad search term still selects
    // everything it matches even past the "+N more - type to narrow down" cutoff shown on screen.
    // Merges into whatever's already checked, same non-destructive reasoning togglePartyType uses.
    function selectAllFilteredDropdownOptions(dd) {
      const query = dropdownSearch[dd.key].trim().toLowerCase();
      const matches = !dd.searchable || !query ? dd.options : dd.options.filter((opt) => opt.toLowerCase().includes(query));
      if (!matches.length) return;
      if ('limeIQKey' === dd.key) umrRowFlagFilter.value = false;
      if ('flag' === dd.key) selectedFlag.value = null;
      filters[dd.key] = Array.from(new Set([...filters[dd.key], ...matches]));
    }

    // Clicking a Claim Values, Dates & Details row drills into that one claim
    // by reusing the EXISTING Claim Reference filter - not a separate
    // "selected claim" state - so it cross-filters everything the Claim
    // Reference dropdown already does for free through the existing watch()/
    // fetchFilteredData pipeline: KPIs, the flag panels, and (via /report)
    // this same table plus Additional Claim Details both narrow to that one
    // claim, and the Claim Reference dropdown's own summary text reflects the
    // selection. Clicking the same row again (or clearing/changing the Claim
    // Reference filter another way) restores the full view.
    function isMergedRowSelected(row) {
      return filters.claimReference.length === 1 && filters.claimReference[0] === row.claim_reference;
    }

    function selectMergedClaim(row) {
      filters.claimReference = isMergedRowSelected(row) ? [] : [row.claim_reference];
    }

    // Same toggle-to-select/click-again-to-clear pattern as selectMergedClaim above, just reusing
    // filters.limeIQKey (the LimeIQ Key dropdown's own multi-select state) instead of
    // filters.claimReference - clicking a row in the bell icon's UMR table narrows every other
    // table/KPI/panel down to that one program the same way the LimeIQ Key checklist filter
    // already does, via the same watch()-triggered refetch. ADDITIONALLY sets umrRowFlagFilter,
    // so (unlike just picking the same key from the LimeIQ Key dropdown) this also restricts
    // Applicable Validation Columns/Additional Claim Details to that program's claims that
    // currently have at least one flag failing - see umrRowFlagFilter's own comment for why that's
    // a separate flag rather than reusing filters.flag/severity.
    function isUmrRowSelected(umr) {
      return filters.limeIQKey.length === 1 && filters.limeIQKey[0] === umr.limeiq_key;
    }

    function selectUmrRow(umr) {
      const alreadySelected = isUmrRowSelected(umr);
      filters.limeIQKey = alreadySelected ? [] : [umr.limeiq_key];
      umrRowFlagFilter.value = !alreadySelected;
    }

    // ---- Report Period tree (same pattern as RecProductionReport.vue) ----

    function isYearExpanded(year) {
      return expandedYears.value.includes(year);
    }

    function toggleYearExpanded(year) {
      expandedYears.value = isYearExpanded(year)
        ? expandedYears.value.filter((y) => y !== year)
        : [...expandedYears.value, year];
    }

    function isYearSelected(group) {
      return group.months.length > 0 && group.months.every((m) => selectedPeriods.value.includes(m.value));
    }

    function isYearPartiallySelected(group) {
      const count = group.months.filter((m) => selectedPeriods.value.includes(m.value)).length;
      return count > 0 && count < group.months.length;
    }

    function toggleYear(group) {
      const values = group.months.map((m) => m.value);
      selectedPeriods.value = isYearSelected(group)
        ? selectedPeriods.value.filter((v) => !values.includes(v))
        : [...new Set([...selectedPeriods.value, ...values])];
    }

    const allPeriodsSelected = computed(
      () =>
        reportPeriodOptionsList.value.length > 0 &&
        reportPeriodOptionsList.value.every((p) => selectedPeriods.value.includes(p.value))
    );

    function toggleAllPeriods() {
      selectedPeriods.value = allPeriodsSelected.value ? [] : reportPeriodOptionsList.value.map((p) => p.value);
    }

    // ---- Party dropdown (same grouped-checklist + flyout pattern as
    // RecProductionReport.vue's own Party/DCA dropdown) ----

    function partyKey(partyType, name) {
      return `${partyType}::${name}`;
    }

    function isPartyNameSelected(partyType, name) {
      return filters.party.includes(partyKey(partyType, name));
    }

    function togglePartyName(partyType, name) {
      const key = partyKey(partyType, name);
      filters.party = filters.party.includes(key)
        ? filters.party.filter((k) => k !== key)
        : [...filters.party, key];
    }

    function isPartyTypeSelected(group) {
      return group.names.length > 0 && group.names.every((name) => isPartyNameSelected(group.partyType, name));
    }

    function isPartyTypePartiallySelected(group) {
      const selectedCount = group.names.filter((name) => isPartyNameSelected(group.partyType, name)).length;
      return selectedCount > 0 && selectedCount < group.names.length;
    }

    function togglePartyType(group) {
      const allKeys = group.names.map((name) => partyKey(group.partyType, name));
      filters.party = isPartyTypeSelected(group)
        ? filters.party.filter((k) => !allKeys.includes(k))
        : [...new Set([...filters.party, ...allKeys])];
    }

    // Same "select all currently displayed/filtered options" Enter handler as
    // selectAllFilteredDropdownOptions above, for the Party dropdown's own top-level search -
    // flattens every currently-matching group's own names (filteredPartyGroups, already narrowed
    // by partySearch) into their composite "PartyType::Name" keys, same shape togglePartyType uses.
    function selectAllFilteredPartyGroups() {
      const keys = filteredPartyGroups.value.flatMap((group) =>
        group.names.map((name) => partyKey(group.partyType, name))
      );
      if (!keys.length) return;
      filters.party = Array.from(new Set([...filters.party, ...keys]));
    }

    // Empty sections (e.g. "TPA (0)") can't be selected, so they're ignored here rather than
    // keeping "Select all" permanently unchecked.
    const allPartySelected = computed(() => {
      const nonEmptyGroups = partyGroupsList.value.filter((group) => group.names.length > 0);
      return nonEmptyGroups.length > 0 && nonEmptyGroups.every((group) => isPartyTypeSelected(group));
    });

    function toggleAllParty() {
      filters.party = allPartySelected.value
        ? []
        : partyGroupsList.value.flatMap((group) => group.names.map((name) => partyKey(group.partyType, name)));
    }

    const partySummaryText = computed(() => {
      if (filters.party.length === 0) return 'All';
      if (filters.party.length === 1) return filters.party[0].split('::')[1];
      return `${filters.party.length} selected`;
    });

    const openPartyTypeNames = computed(() => {
      const group = partyGroupsList.value.find((g) => g.partyType === openPartyType.value);
      return group ? group.names : [];
    });

    // "Underwriter" is lead-only data server-side, same reasoning
    // RecProductionReport.vue's dcaSectionLabel relabels it "Lead Underwriter"
    // for display - the underlying key/dca_selections wire format stays the
    // literal "Underwriter" the backend expects, only the label shown to the
    // user changes.
    function partyTypeLabel(partyType) {
      return 'Underwriter' === partyType ? 'Lead Underwriter' : partyType;
    }

    const partyFlyoutSearch = ref('');

    const filteredOpenPartyTypeNames = computed(() => {
      const query = partyFlyoutSearch.value.trim().toLowerCase();
      if (!query) return openPartyTypeNames.value;
      return openPartyTypeNames.value.filter((name) => name.toLowerCase().includes(query));
    });

    // Same "select all currently displayed/filtered options" Enter handler as
    // selectAllFilteredPartyGroups above, for this flyout's own search box (a single Party Type's
    // names, narrowed by partyFlyoutSearch rather than partySearch).
    function selectAllFilteredOpenPartyTypeNames() {
      if (!openPartyType.value) return;
      const names = filteredOpenPartyTypeNames.value;
      if (!names.length) return;
      const keys = names.map((name) => partyKey(openPartyType.value, name));
      filters.party = Array.from(new Set([...filters.party, ...keys]));
    }

    // Top-level search, shown as soon as the dropdown opens (matching the
    // Severity/Flag/Claim Reference/LimeIQ Key dropdowns' own search-on-open
    // behavior) - unlike those, a Party "option" is a whole group of names,
    // not a single string, so this matches against the group's own label OR
    // any name inside it. Every section stays listed while searching (LIQ-2443 -
    // typing "Dale" used to hide TPA/Broker entirely), each narrowed to just its
    // matching names, so a section with no matches reads e.g. "TPA (0)". Because
    // `names` here is the narrowed list, the section's checkbox (isPartyTypeSelected/
    // togglePartyType) only checks/selects the search matches, not the whole group
    // (LIQ-2468).
    const partySearch = ref('');

    const filteredPartyGroups = computed(() => {
      const query = partySearch.value.trim().toLowerCase();
      if (!query) return partyGroupsList.value;
      return partyGroupsList.value.map((group) => ({
        partyType: group.partyType,
        names: partyTypeLabel(group.partyType).toLowerCase().includes(query)
          ? group.names
          : group.names.filter((name) => name.toLowerCase().includes(query)),
      }));
    });

    // Positions the portaled flyout next to whichever party-type row was
    // clicked, flipping to the row's left when it wouldn't fit on the right -
    // same idea as RecProductionReport.vue's own togglePartyTypeFlyout, just
    // without that version's DCA_FLYOUT_WIDTH sync since this flyout's own
    // CSS width is a fixed constant here too (see .bdx-party-flyout).
    const PARTY_FLYOUT_WIDTH = 220;

    function togglePartyFlyout(partyType, event) {
      // Carry the top-level search into the flyout (unless it matched the section's own label,
      // e.g. "tpa"), so opening "Lead Underwriter (1)" after searching "Dale" lists that one match.
      const query = partySearch.value.trim();
      partyFlyoutSearch.value =
        query && !partyTypeLabel(partyType).toLowerCase().includes(query.toLowerCase()) ? query : '';
      if (openPartyType.value === partyType) {
        openPartyType.value = null;
        return;
      }
      openPartyType.value = partyType;
      // Read synchronously - the browser clears event.currentTarget once dispatch ends, so
      // reading it inside nextTick always got null and the flyout was never positioned
      // (LIQ-2468: "group arrows don't open").
      const row = event.currentTarget;
      nextTick(() => {
        if (!row || !partyFlyoutRef.value) return;
        const rect = row.getBoundingClientRect();
        const overflowsRight = rect.right + PARTY_FLYOUT_WIDTH + 8 > window.innerWidth;
        partyFlyoutStyle.top = `${rect.top}px`;
        partyFlyoutStyle.left = overflowsRight
          ? `${Math.max(8, rect.left - PARTY_FLYOUT_WIDTH - 8)}px`
          : `${rect.right + 8}px`;
      });
    }

    function closePartyFlyout() {
      openPartyType.value = null;
      partyFlyoutSearch.value = '';
      partySearch.value = '';
    }

    // Populated from GET /kpis (see fetchFilteredData) - 0 until the first
    // fetch resolves.
    const kpis = reactive({
      bdxWithFlag: 0,
      claimsWithFlag: 0,
      bdxWithCriticalFlag: 0,
      claimsWithCriticalFlags: 0,
    });

    const kpiTiles = computed(() => [
      { key: 'bdxWithFlag', label: 'BDX w/ Flag', value: kpis.bdxWithFlag },
      { key: 'claimsWithFlag', label: 'Claims w/ Flag', value: kpis.claimsWithFlag },
      { key: 'bdxWithCriticalFlag', label: 'BDX w/ Critical Flag', value: kpis.bdxWithCriticalFlag },
      { key: 'claimsWithCriticalFlags', label: 'Claims w/ Critical Flags', value: kpis.claimsWithCriticalFlags },
    ]);

    const bdxStatusTristateClass = computed(() => {
      if (bdxStatus.value === 'active') return 'bdx-tristate--left bdx-tristate--green';
      if (bdxStatus.value === 'inactive') return 'bdx-tristate--right bdx-tristate--red';
      return 'bdx-tristate--center';
    });

    // Single click target advancing the knob one step to the right each click, wrapping back to
    // the start - replaces the old 3-independently-clickable-zones interaction (left/center/right
    // each jumping straight to their own state in one click), per explicit request. left->center->
    // right->left..., matching the knob's own visual left-to-right order (see
    // bdxStatusTristateClass just above).
    const BDX_STATUS_CYCLE = ['active', 'all', 'inactive'];
    function advanceBdxStatus() {
      const currentIndex = BDX_STATUS_CYCLE.indexOf(bdxStatus.value);
      bdxStatus.value = BDX_STATUS_CYCLE[(currentIndex + 1) % BDX_STATUS_CYCLE.length];
    }

    const claimStatusTristateClass = computed(() => {
      if (claimStatus.value === 'open') return 'bdx-tristate--left bdx-tristate--green';
      if (claimStatus.value === 'closed') return 'bdx-tristate--right bdx-tristate--red';
      return 'bdx-tristate--center';
    });

    // Same single-click-advances-cyclically treatment as advanceBdxStatus above. claimStatus can
    // also be 'blank' (set via the Claim Status COLUMN's own menu, not this toggle - see
    // claimStatusMenuOptions) - not one of this toggle's own 3 positions, so indexOf returns -1
    // there; (-1 + 1) % 3 = 0 lands cleanly on 'open' (the first position) rather than throwing or
    // needing a separate guard.
    const CLAIM_STATUS_TOGGLE_CYCLE = ['open', 'all', 'closed'];
    function advanceClaimStatusToggle() {
      const currentIndex = CLAIM_STATUS_TOGGLE_CYCLE.indexOf(claimStatus.value);
      claimStatus.value = CLAIM_STATUS_TOGGLE_CYCLE[(currentIndex + 1) % CLAIM_STATUS_TOGGLE_CYCLE.length];
    }

    const bdxStatusLabel = computed(() => {
      if (bdxStatus.value === 'active') return 'Active';
      if (bdxStatus.value === 'inactive') return 'Inactive';
      return 'All';
    });

    const claimStatusLabel = computed(() => {
      if (claimStatus.value === 'open') return 'Claims (Open)';
      if (claimStatus.value === 'closed') return 'Claims (Closed)';
      if (claimStatus.value === 'blank') return 'Claims (Blank)';
      return 'Claim Status?';
    });

    // Replaces the old Severity checklist dropdown (removed from
    // filterDropdowns above) with a tristate toggle, same pattern as BDX
    // Status/Claim Status. Derived from filters.severity itself (not a
    // separate ref) so it can't drift out of sync with it - e.g.
    // clearFilters() resetting filters.severity = [] correctly snaps this
    // back to the middle/"all" position for free, and filters.severity stays
    // the single source of truth the existing /report severity param and
    // cross-filter watch() already depend on.
    const severityToggleState = computed(() => {
      if (filters.severity.length === 1 && filters.severity[0] === 'Non-Critical') return 'noncritical';
      if (filters.severity.length === 1 && filters.severity[0] === 'Critical') return 'critical';
      return 'all';
    });

    const severityTristateClass = computed(() => {
      if (severityToggleState.value === 'noncritical') return 'bdx-tristate--left bdx-tristate--green';
      if (severityToggleState.value === 'critical') return 'bdx-tristate--right bdx-tristate--red';
      return 'bdx-tristate--center';
    });

    const severityLabel = computed(() => {
      if (severityToggleState.value === 'noncritical') return 'Non Critical';
      if (severityToggleState.value === 'critical') return 'Critical';
      return 'Severity?';
    });

    function setSeverityToggle(state) {
      filters.severity = state === 'noncritical' ? ['Non-Critical'] : state === 'critical' ? ['Critical'] : [];
    }

    // Same single-click-advances-cyclically treatment as advanceBdxStatus/
    // advanceClaimStatusToggle above - severityToggleState collapses filters.severity down to
    // exactly one of these 3 values already (see its own computed), so no out-of-cycle value to
    // guard against the way claimStatus' own 'blank' needed.
    const SEVERITY_TOGGLE_CYCLE = ['noncritical', 'all', 'critical'];
    function advanceSeverityToggle() {
      const currentIndex = SEVERITY_TOGGLE_CYCLE.indexOf(severityToggleState.value);
      setSeverityToggle(SEVERITY_TOGGLE_CYCLE[(currentIndex + 1) % SEVERITY_TOGGLE_CYCLE.length]);
    }

    function barWidth(count, max) {
      // No artificial floor - lower counts should keep shrinking proportionally
      // relative to the list's max, same as the live report, instead of every
      // small value bottoming out at the same clamped width. __bar's own
      // min-width in CSS is the only floor, and it's small enough (1px) to
      // stay just barely visible rather than flattening these differences.
      return `${(count / max) * 100}%`;
    }

    function isCountInsideBar(count, max) {
      // The count now always renders centered in the column (see the SCSS for
      // __count), so "inside" (white text) only reads correctly once the bar
      // itself reaches the midpoint - short of that, the center point is still
      // on the plain background and needs dark text instead.
      return (count / max) * 100 > 50;
    }

    // Highlights a flag row whenever it's part of the CURRENT filters.flag selection - covers
    // both selectFlag's own single-row-click drill-down (which sets filters.flag = [flag.name])
    // and the Flag dropdown's own multi-select checklist (filters.flag can hold several names at
    // once there) with the same check, so checking multiple flags in the dropdown highlights all
    // of them in these panels rather than requiring a separate multi-select highlight path.
    // severity is unused now (a flag name is unique to one severity in the real catalog data, so
    // filters.flag.includes(...) alone can't cross-panel-collide) - kept as a param so the
    // template's own two call sites (#head(critical)/#head(non-critical)) don't need to change.
    function isFlagSelected(flag) {
      return filters.flag.includes(flag.name);
    }

    // Reuses filters.flag (the Flag dropdown's own multi-select checklist
    // state) rather than introducing separate state, same technique as
    // selectMergedClaim's own reuse of filters.claimReference - clicking a
    // flag row now both controls column visibility (selectedFlag, unchanged
    // from before) AND cross-filters every table/KPI/panel down to just this
    // flag, via the exact same /report `flag` param + watch()-triggered
    // refetch the checklist dropdown already drives. Wholesale-replaces
    // filters.flag (not toggles a single entry within it) since clicking a
    // flag row is a "drill into just this one" gesture, not an
    // add-to-multi-select one.
    function selectFlag(flag, severity) {
      const alreadySelected = isFlagSelected(flag, severity);
      selectedFlag.value = alreadySelected ? null : { ...flag, severity };
      filters.flag = alreadySelected ? [] : [flag.name];
    }

    function toggleFlagTab(tab) {
      // tab is one of the real ValidationRules Group values ('Dates' | 'Details' |
      // 'Values' | 'Missing'), so this both highlights the button and filters the
      // two flag lists below to that group.
      activeFlagTab.value = activeFlagTab.value === tab ? null : tab;
    }

    const visibleCriticalFlags = computed(() =>
      criticalFlagsList.value.filter((f) => !activeFlagTab.value || f.group === activeFlagTab.value)
    );
    const visibleNonCriticalFlags = computed(() =>
      nonCriticalFlagsList.value.filter((f) => !activeFlagTab.value || f.group === activeFlagTab.value)
    );
    // Scaled against the FULL list's max (not the tab-filtered subset) - matches
    // the live report, where most non-critical bars are invisible slivers next to
    // Policy or Group Ref Missing's 91,514, regardless of which tab is active.
    // Now a computed (the underlying lists are live) with a 1 floor so an
    // empty/loading list can't divide-by-zero barWidth into NaN%. A plain
    // reduce, not Math.max(1, ...list) - spreading a live-fetched (unbounded
    // size) array as call arguments hits V8's argument-count limit and
    // throws "Maximum call stack size exceeded" once the list is large
    // enough, unlike the old small hardcoded list this replaced.
    const maxCriticalCount = computed(() => criticalFlagsList.value.reduce((max, f) => Math.max(max, f.count), 1));
    const maxNonCriticalCount = computed(() =>
      nonCriticalFlagsList.value.reduce((max, f) => Math.max(max, f.count), 1)
    );

    const flagDescriptionText = computed(() => {
      if (!selectedFlag.value) {
        return 'Please select Validation to show description';
      }
      return selectedFlag.value.description;
    });

    // Reverse-lookup ParamName label -> our column key, for each source table's
    // swappable columns - built off the original CLAIM_VALUES_COLUMNS/
    // CLAIM_DETAILS_COLUMNS (whose labels the report's own ALLOWED_PARAMS maps
    // reference), even though the template itself now renders MERGED_COLUMNS.
    const claimValuesKeyByLabel = new Map(CLAIM_VALUES_COLUMNS.map((c) => [c.label, c.key]));
    const claimDetailsKeyByLabel = new Map(CLAIM_DETAILS_COLUMNS.map((c) => [c.label, c.key]));

    const mergedFixedColumns = MERGED_COLUMNS.filter((c) => c.key === 'claim_reference');
    const DETAILS_KEYS = new Set(CLAIM_DETAILS_COLUMNS.filter((c) => c.key !== 'claim_reference').map((c) => c.key));
    const mergedDetailsSwappableColumns = MERGED_COLUMNS.filter((c) => DETAILS_KEYS.has(c.key));
    const mergedValuesSwappableColumns = MERGED_COLUMNS.filter(
      (c) => c.key !== 'claim_reference' && !DETAILS_KEYS.has(c.key)
    );

    // Combines both tables' own flag-visibility rules (values fails open,
    // details collapses to just Claim Reference when unmapped) into one
    // column list for the merged table, in MERGED_COLUMNS' own order -
    // Claim Reference, then Claim Status/Denial/Refer to UW, then the 27
    // values/dates columns.
    const visibleMergedColumns = computed(() => {
      if (!selectedFlag.value) {
        return MERGED_COLUMNS;
      }

      const valuesAllowedLabels = flagColumnMap.valueParams[selectedFlag.value.flagColumn];
      const detailsAllowedLabels = flagColumnMap.detailParams[selectedFlag.value.flagColumn];
      // LIQ-2307 (2026-09-23) - a flag with a REAL mapping on one scope but none on the other (e.g.
      // Refer_to_Underwriters_Missing: detail-only, no value columns are relevant to it at all) no
      // longer falls back to fail-open on the unmapped side - it hides that side entirely instead,
      // by explicit request. Only a flag with NO mapping on EITHER scope (nothing defined for it
      // yet, e.g. Coverholder_Missing) still falls back to flagColumnMap.valueFailOpen - there's
      // nothing to conclude "not relevant" from for those, unlike a flag that DOES specify one side.
      const hasAnyMapping = Boolean(valuesAllowedLabels || detailsAllowedLabels);

      const visibleValuesColumns = valuesAllowedLabels
        ? mergedValuesSwappableColumns.filter((c) =>
            valuesAllowedLabels.some((label) => claimValuesKeyByLabel.get(label) === c.key)
          )
        : hasAnyMapping
        ? []
        : flagColumnMap.valueFailOpen
        ? mergedValuesSwappableColumns
        : [];

      const visibleDetailsColumns = detailsAllowedLabels
        ? mergedDetailsSwappableColumns.filter((c) =>
            detailsAllowedLabels.some((label) => claimDetailsKeyByLabel.get(label) === c.key)
          )
        : flagColumnMap.detailFailOpen
        ? mergedDetailsSwappableColumns
        : []; // fail closed - an unmapped flag collapses to just Claim Reference

      return [...mergedFixedColumns, ...visibleDetailsColumns, ...visibleValuesColumns];
    });

    function clearFilters() {
      bdxStatus.value = 'active';
      claimStatus.value = 'all';
      selectedPeriods.value = [];
      expandedYears.value = periodTree.value.map((group) => group.year);
      activeFlagTab.value = null;
      selectedFlag.value = null;
      filters.severity = [];
      filters.flag = [];
      filters.claimReference = [];
      filters.limeIQKey = [];
      filters.umr = [];
      filters.party = [];
      umrRowFlagFilter.value = false;
      openPartyType.value = null;
      partyFlyoutSearch.value = '';
      partySearch.value = '';
      dropdownSearch.flag = '';
      dropdownSearch.claimReference = '';
      dropdownSearch.limeIQKey = '';
      dropdownSearch.umr = '';
      umrColumnSearch.value = {};
      umrSortBy.value = null;
      umrSortDesc.value = false;
      additionalColumnSearch.value = {};
      additionalSortBy.value = null;
      additionalSortDesc.value = false;
    }

    // Per-filter clear icons (see the template's own bdx-filter-clear-icon usages) - each resets
    // just ONE filter to clearFilters' own default for it, rather than everything at once.
    function clearBdxStatusFilter() {
      bdxStatus.value = 'active';
    }

    function clearClaimStatusFilter() {
      claimStatus.value = 'all';
    }

    function clearSeverityFilter() {
      filters.severity = [];
    }

    function clearPartyFilter() {
      filters.party = [];
      closePartyFlyout();
    }

    // Shared by Flag/Claim Reference/LimeIQ Key's own clear icons (the filterDropdowns loop) -
    // flag additionally clears selectedFlag, since selectFlag() always sets both together (see its
    // own comment) and leaving selectedFlag stale after clearing filters.flag would leave the flag
    // panel's own row highlighting/column-visibility narrowing pointed at a flag that's no longer
    // actually selected.
    function clearDropdownFilter(key) {
      filters[key] = [];
      if ('flag' === key) selectedFlag.value = null;
    }

    // Table-level "clear all column filters" icon (see the template's own
    // bdx-panel-title-actions usage) - also resets claimStatus/selectedPeriods, even though both
    // already have their OWN dedicated clear icon elsewhere (top bar/sidebar): Claim Status and
    // Reporting Period are themselves rendered as column-header filters in THIS table (their own
    // dropdown menus live in the <th> row here), so a user who filtered via one of those two
    // columns expects this same eraser to undo it, not just the icons elsewhere on the page.
    // Reported live: filtering Claim Status to "Blank" via its column menu left this icon hidden
    // (hasAdditionalColumnFilters below didn't know about it either) with no way to clear it from
    // this table at all.
    function clearAdditionalColumnFilters() {
      additionalColumnSearch.value = {};
      additionalSortBy.value = null;
      additionalSortDesc.value = false;
      claimStatus.value = 'all';
      selectedPeriods.value = [];
      // UMR has no dedicated top-bar clear icon of its own (unlike Claim Reference/LimeIQ Key/TPA,
      // which reuse existing top-level filter state with existing clear icons elsewhere) - same
      // reasoning as claimStatus/selectedPeriods above, so this eraser is its only way to clear.
      filters.umr = [];
      dropdownSearch.umr = '';
    }

    function clearUmrColumnFilters() {
      umrColumnSearch.value = {};
      umrSortBy.value = null;
      umrSortDesc.value = false;
    }

    // Same "clear all column filters" treatment as clearAdditionalColumnFilters above, for
    // Applicable Validation Columns - no claimStatus/selectedPeriods reset needed here, since this
    // table has no column-header menu driving either of those (both are the OTHER table's own
    // special-cased columns).
    function clearMergedColumnFilters() {
      mergedColumnSearch.value = {};
      mergedSortBy.value = null;
      mergedSortDesc.value = false;
    }

    const hasAdditionalColumnFilters = computed(
      () =>
        Object.values(additionalColumnSearch.value).some(isColumnSearchValueActive) ||
        !!additionalSortBy.value ||
        'all' !== claimStatus.value ||
        !!selectedPeriods.value.length ||
        !!filters.umr.length
    );
    const hasUmrColumnFilters = computed(
      () => Object.values(umrColumnSearch.value).some(Boolean) || !!umrSortBy.value
    );
    const hasMergedColumnFilters = computed(
      () => Object.values(mergedColumnSearch.value).some(isColumnSearchValueActive) || !!mergedSortBy.value
    );

    // String-slicing on the ISO "YYYY-MM-DD" prefix rather than `new Date(value)` - these are
    // pure calendar dates with no time-of-day meaning, and parsing one via the Date constructor
    // reads it as UTC midnight, then formatting via local getMonth()/getDate() can shift the
    // displayed day by one depending on the viewer's own timezone offset. Plain string slicing
    // has no such pitfall.
    function formatDateMDY(value) {
      if (value === '' || value === null || value === undefined) {
        return '';
      }
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
      if (!match) return value;
      const [, year, month, day] = match;
      return `${month}-${day}-${year}`;
    }

    function formatCell(value, key) {
      if (value === '' || value === null || value === undefined) {
        return '';
      }
      if (CURRENCY_COLUMN_KEYS.has(key) && typeof value === 'number') {
        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (DATE_COLUMN_KEYS.has(key)) {
        return formatDateMDY(value);
      }
      return value;
    }

    // Strips anything that isn't filesystem-safe from one filter value before it goes into an
    // exported file name (party/flag/claim reference/limeIQ key values can contain spaces,
    // slashes, ampersands, etc.).
    function sanitizeForFileName(value) {
      return String(value).replace(/[^a-zA-Z0-9]+/g, '');
    }

    // Every export on this report (downloadReportExcel/downloadAdditionalDetailsExcel/
    // downloadUmrsExcel) appends the currently-applied filters to its own file name via this, so
    // a downloaded file is self-describing (e.g. which Report Period/TPA/flag it was scoped to)
    // without needing to keep the report open to remember what was selected. Multi-select filters
    // with more than 2 values collapse to a count (e.g. "3Flags") rather than every value, to keep
    // file names from becoming unreasonably long.
    function buildExportFileNameSuffix() {
      const parts = [];

      if (bdxStatus.value === 'active') parts.push('BDXActive');
      else if (bdxStatus.value === 'inactive') parts.push('BDXInactive');

      if (claimStatus.value === 'open') parts.push('ClaimsOpen');
      else if (claimStatus.value === 'closed') parts.push('ClaimsClosed');
      else if (claimStatus.value === 'blank') parts.push('ClaimsBlank');

      if (selectedPeriods.value.length) {
        const periodLabels = selectedPeriods.value
          .slice()
          .sort()
          .map((p) => sanitizeForFileName(formatReportPeriodLabel(p)));
        parts.push(periodLabels.length > 2 ? `${periodLabels.length}Periods` : periodLabels.join('-'));
      }

      if (filters.severity.length) parts.push(filters.severity.map(sanitizeForFileName).join('-'));

      if (filters.flag.length) {
        parts.push(
          filters.flag.length > 2 ? `${filters.flag.length}Flags` : filters.flag.map(sanitizeForFileName).join('-')
        );
      }

      if (filters.party.length) {
        parts.push(
          filters.party.length > 2
            ? `${filters.party.length}Parties`
            : filters.party.map(sanitizeForFileName).join('-')
        );
      }

      if (filters.claimReference.length) {
        parts.push(
          filters.claimReference.length > 2
            ? `${filters.claimReference.length}Claims`
            : filters.claimReference.map(sanitizeForFileName).join('-')
        );
      }

      if (filters.limeIQKey.length) {
        parts.push(
          filters.limeIQKey.length > 2
            ? `${filters.limeIQKey.length}Keys`
            : filters.limeIQKey.map(sanitizeForFileName).join('-')
        );
      }

      return parts.length ? `_${parts.join('_')}` : '';
    }

    // Same pattern as RecProductionReport.vue's own downloadVarianceExcel: unlike
    // downloadAdditionalDetailsExcel below (which just exports whatever's already loaded on
    // screen), this table's export is the one exception that fetches every row matching the
    // current filters regardless of how much has been paged in via "Load more" - a real
    // GET /report?export=true call (see router.py's own export param), only paid on this explicit
    // action. Exports whatever columns are currently visible (visibleMergedColumns), matching the
    // on-screen flag-driven column set rather than every possible column.
    const reportExporting = ref(false);
    let reportExportCancelSource = null;

    async function downloadReportExcel() {
      loadError.value = '';
      if (reportExportCancelSource) reportExportCancelSource.cancel();
      reportExportCancelSource = axios.CancelToken.source();
      const cancelToken = reportExportCancelSource.token;

      reportExporting.value = true;
      try {
        const params = currentFilterParams();
        const reportRes = await BdxValidationService.getReport({
          ...params,
          flag: filters.flag.length ? filters.flag : undefined,
          severity: filters.severity.length ? filters.severity : undefined,
          has_flag: umrRowFlagFilter.value || undefined,
          export: true,
          cancelToken,
        });
        const reportData = reportRes.data || {};
        const claimDetailsRows = reportData.claim_details || [];
        const mergedRows = (reportData.claim_values || []).map((valuesRow, index) => ({
          ...valuesRow,
          ...claimDetailsRows[index],
        }));

        const columns = visibleMergedColumns.value;
        const humanizedRows = mergedRows.map((row) =>
          Object.fromEntries(
            columns.map((col) => [
              col.label,
              DATE_COLUMN_KEYS.has(col.key) ? formatDateMDY(row[col.key]) : row[col.key],
            ])
          )
        );
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(humanizedRows);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Applicable Validation Columns'.slice(0, 31));
        XLSX.writeFile(workbook, `Applicable_Validation_Columns${buildExportFileNameSuffix()}.xlsx`);
      } catch (err) {
        if (axios.isCancel(err)) return;
        loadError.value =
          (err.response && err.response.data && err.response.data.detail) || 'Failed to export data.';
      } finally {
        reportExporting.value = false;
      }
    }

    // Same pattern as RecProductionReport.vue's own downloadExcel/
    // downloadBdxExcel: a real .xlsx (SheetJS), not export-from-json's 'xls'
    // type (an HTML table renamed with an .xls extension, which Excel flags
    // as a format mismatch for .xlsx). Uses this table's own column labels
    // (already correct for abbreviations like TPA/UMR) rather than deriving
    // header text from the row keys. Same "every matching row, not just what's been paged in via
    // Load more" exception as downloadReportExcel above - a real GET /additional-claim-details
    // ?export=true call, rather than exporting whatever's currently in additionalClaimDetailsRowsList
    // (which under a narrow flag filter can be far fewer rows than actually match - e.g. 100 of 153).
    const additionalExporting = ref(false);
    let additionalExportCancelSource = null;

    async function downloadAdditionalDetailsExcel() {
      loadError.value = '';
      if (additionalExportCancelSource) additionalExportCancelSource.cancel();
      additionalExportCancelSource = axios.CancelToken.source();
      const cancelToken = additionalExportCancelSource.token;

      additionalExporting.value = true;
      try {
        const params = currentFilterParams();
        const additionalRes = await BdxValidationService.getAdditionalClaimDetails({
          ...params,
          flag: filters.flag.length ? filters.flag : undefined,
          severity: filters.severity.length ? filters.severity : undefined,
          has_flag: umrRowFlagFilter.value || undefined,
          sort_by: additionalSortBy.value || undefined,
          sort_dir: additionalSortDesc.value ? 'desc' : undefined,
          export: true,
          cancelToken,
        });
        const rows = (additionalRes.data && additionalRes.data.additional_claim_details) || [];

        const humanizedRows = rows.map((row) =>
          Object.fromEntries(
            ADDITIONAL_CLAIM_DETAILS_COLUMNS.map((col) => [
              col.label,
              DATE_COLUMN_KEYS.has(col.key) ? formatDateMDY(row[col.key]) : row[col.key],
            ])
          )
        );
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(humanizedRows);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Additional Claim Details');
        XLSX.writeFile(workbook, `Additional_Claim_Details${buildExportFileNameSuffix()}.xlsx`);
      } catch (err) {
        if (axios.isCancel(err)) return;
        loadError.value =
          (err.response && err.response.data && err.response.data.detail) || 'Failed to export data.';
      } finally {
        additionalExporting.value = false;
      }
    }

    // umrsList (unlike the two tables above) is never paginated in the first place - GET /umrs
    // always returns every (limeiq_key, report_period) pair matching the current filters in one
    // shot (see fetchUmrs) - so this exports that full list directly, with no separate fetch.
    function downloadUmrsExcel() {
      const humanizedRows = umrsList.value.map((umr) => ({
        'LimeIQ Key': umr.limeiq_key,
        'Current Report Period': umr.report_period ? formatReportPeriodLabel(umr.report_period) : '',
        Critical: umr.critical_count,
        'Non-Critical': umr.non_critical_count,
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(humanizedRows);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Latest UMRs');
      XLSX.writeFile(workbook, `Latest_UMRs${buildExportFileNameSuffix()}.xlsx`);
    }

    return {
      bdxStatus,
      claimStatus,
      bdxStatusTristateClass,
      advanceBdxStatus,
      claimStatusTristateClass,
      advanceClaimStatusToggle,
      bdxStatusLabel,
      claimStatusLabel,
      severityToggleState,
      severityTristateClass,
      severityLabel,
      setSeverityToggle,
      advanceSeverityToggle,
      kpiTiles,
      flagDescriptionText,
      loadingReference,
      loadingFlagsKpis,
      loadingReportInitial,
      loadingReportMore,
      reportTotalRows,
      hasMoreReport,
      reportLoadMoreCount,
      loadMoreReport,
      loadingAdditionalInitial,
      loadingAdditionalMore,
      additionalTotalRows,
      hasMoreAdditional,
      additionalLoadMoreCount,
      loadMoreAdditional,
      loadError,
      dismissLoadError,
      selectedPeriods,
      periodTree,
      isYearExpanded,
      toggleYearExpanded,
      isYearSelected,
      isYearPartiallySelected,
      toggleYear,
      allPeriodsSelected,
      toggleAllPeriods,
      partySearch,
      filteredPartyGroups,
      openPartyType,
      partyFlyoutRef,
      partyFlyoutStyle,
      openPartyTypeNames,
      partyTypeLabel,
      partyFlyoutSearch,
      filteredOpenPartyTypeNames,
      isPartyNameSelected,
      togglePartyName,
      selectAllFilteredOpenPartyTypeNames,
      isPartyTypeSelected,
      isPartyTypePartiallySelected,
      togglePartyType,
      selectAllFilteredPartyGroups,
      tpaGroup,
      tpaColumnMenuSearch,
      filteredTpaColumnMenuNames,
      selectAllFilteredTpaColumnMenuNames,
      tpaColumnMenuOpen,
      tpaColumnMenuRef,
      tpaColumnMenuStyle,
      toggleTpaColumnMenu,
      allPartySelected,
      toggleAllParty,
      partySummaryText,
      togglePartyFlyout,
      closePartyFlyout,
      filters,
      dropdownSearch,
      filterDropdowns,
      filterSummary,
      filteredDropdownOptions,
      dropdownOptionsTruncatedCount,
      isAllSelected,
      toggleAllOptions,
      toggleFilterOption,
      selectAllFilteredDropdownOptions,
      claimReferenceDropdownConfig,
      claimReferenceColumnMenuOpen,
      claimReferenceColumnMenuRef,
      claimReferenceColumnMenuStyle,
      toggleClaimReferenceColumnMenu,
      umrDropdownConfig,
      umrColumnMenuOpen,
      umrColumnMenuRef,
      umrColumnMenuStyle,
      toggleUmrColumnMenu,
      limeIQKeyDropdownConfig,
      limeIQKeyColumnMenuOpen,
      limeIQKeyColumnMenuRef,
      limeIQKeyColumnMenuStyle,
      toggleLimeIQKeyColumnMenu,
      isMergedRowSelected,
      selectMergedClaim,
      isUmrRowSelected,
      selectUmrRow,
      clearFilters,
      clearBdxStatusFilter,
      clearClaimStatusFilter,
      clearSeverityFilter,
      clearPartyFilter,
      clearDropdownFilter,
      clearAdditionalColumnFilters,
      clearUmrColumnFilters,
      clearMergedColumnFilters,
      hasAdditionalColumnFilters,
      hasUmrColumnFilters,
      hasMergedColumnFilters,
      clearFiltersIcon,
      notificationsIcon,
      filterActiveIcon,
      formatReportPeriodLabel,
      umrsList,
      loadingUmrs,
      umrColumns: UMR_COLUMNS,
      umrColumnSearch,
      umrColumnSearchMenuRef,
      setUmrColumnSearch,
      umrSortBy,
      umrSortDesc,
      toggleUmrColumnSort,
      displayedUmrsList,
      visibleMergedColumns,
      mergedClaimRows: mergedClaimRowsList,
      mergedColumnSearch,
      mergedColumnSearchMenuRef,
      setMergedColumnSearch,
      mergedColumnSearchType,
      isMergedColumnFiltered,
      mergedSortBy,
      mergedSortDesc,
      toggleMergedColumnSort,
      displayedMergedRows,
      mergedColumnStyle,
      startMergedColumnResize,
      additionalClaimDetailsColumns: ADDITIONAL_CLAIM_DETAILS_COLUMNS,
      additionalClaimDetailsRows: additionalClaimDetailsRowsList,
      additionalColumnSearch,
      additionalColumnSearchMenuRef,
      setAdditionalColumnSearch,
      additionalColumnSearchType,
      isAdditionalColumnFiltered,
      reportPeriodColumnMenuOpen,
      reportPeriodColumnMenuRef,
      reportPeriodColumnMenuStyle,
      toggleReportPeriodColumnMenu,
      additionalColumnStyle,
      startAdditionalColumnResize,
      additionalSortBy,
      additionalSortDesc,
      toggleAdditionalColumnSort,
      displayedAdditionalClaimDetailsRows,
      inlineOptionsMenuRef,
      claimStatusMenuOptions,
      setClaimStatusFilter,
      downloadAdditionalDetailsExcel,
      additionalExporting,
      downloadReportExcel,
      reportExporting,
      downloadUmrsExcel,
      formatCell,
      flagTabs: [
        { value: 'Dates', label: 'Dates' },
        { value: 'Details', label: 'Details' },
        { value: 'Values', label: 'Values' },
        { value: 'Missing', label: 'Missing' },
      ],
      activeFlagTab,
      toggleFlagTab,
      criticalFlags: visibleCriticalFlags,
      nonCriticalFlags: visibleNonCriticalFlags,
      maxCriticalCount,
      maxNonCriticalCount,
      barWidth,
      isCountInsideBar,
      isFlagSelected,
      selectFlag,
    };
  },
});
</script>

<style lang="scss" scoped>
@import './BDXValidationReport.scss';
</style>

<style lang="scss">
// Unscoped on purpose, same reasoning as RecProductionReport.vue's own copy of
// this block: .router-view and its parent shell are ancestors outside this
// component's template, so scoped CSS (even ::v-deep) can never reach them.
// Both classes are added/removed by this component's own mount/unmount hooks
// above, so neither override ever applies outside this report being shown -
// distinct class names from FCP's own (bdx-report-* vs fcp-report-*) so the two
// reports' height-fill tagging can never collide if both somehow mounted.
.bdx-report-shell-active {
  display: flex !important;
  flex-direction: column !important;
  min-height: 0;

  > *:not(.router-view) {
    flex-shrink: 0;
  }
}

.router-view.bdx-report-active {
  align-items: stretch !important;
  justify-content: flex-start !important;
  flex: 1 1 auto !important;
  min-height: 0 !important;
}
</style>
